import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  SiteSettings,
  Category,
  Brand,
  Product,
  CartItem,
  Order,
  Coupon,
  FilterState,
  User,
  ProductVariant,
  Banner,
  BlogPost,
  Review,
  Address,
  ProductDisplay
} from '../types';
import { initialSiteSettings, initialCategories, initialBrands, initialProducts, initialBanners, initialCoupons } from '../data/seedData';
import { initialBlogPosts } from '../data/blogPosts';
import { adminFetch } from '../utils/apiClient';
import { sendClientOrderEmail } from '../lib/clientMailer';
import { formatDateIST } from '../utils/dateUtils';
import { sanitizeImageUrl, DEFAULT_FALLBACK_IMAGE } from '../utils/imageOptimizer';

export const initialProductDisplay: ProductDisplay = {
  id: 'default',
  dealsOfTheDay: [
    'p-1786951004136', 'p-1786983081915', 'p-1787083840983', 'p-1787150222306',
    'prod_mt-kurta_1', 'prod_mt-kurta_14', 'prod_mt-kurta_2', 'prod_mt-kurta_3',
    'prod_wt-salwar_1', 'prod_wt-salwar_2', 'prod_wt-salwar_3', 'prod_wt-saree_10',
    'prod_wt-saree_14', 'prod_wt-saree_15', 'prod_wt-saree_19', 'prod_wt-saree_20',
    'prod_wt-saree_22', 'prod_wt-saree_25', 'prod_wt-saree_27', 'prod_wt-saree_28',
    'prod_wt-saree_29'
  ],
  dealsCount: 21,
  newArrivals: [
    'p-1786951004136', 'p-1786957059867', 'p-1786983081915', 'p-1787083840983',
    'p-1787150222306', 'prod_kt-b-ethnic_12', 'prod_kt-b-ethnic_14', 'prod_kt-b-ethnic_18',
    'prod_kt-b-ethnic_2', 'prod_kt-b-ethnic_20', 'prod_kt-b-ethnic_24', 'prod_kt-b-ethnic_26',
    'prod_kt-b-ethnic_30', 'prod_kt-b-jeans_12', 'prod_kt-b-jeans_14', 'prod_kt-b-jeans_18',
    'prod_kt-b-jeans_2', 'prod_kt-b-jeans_20', 'prod_kt-b-jeans_24', 'prod_kt-b-jeans_26',
    'prod_kt-b-jeans_30', 'prod_kt-b-tshirts_12', 'prod_kt-b-tshirts_14', 'prod_kt-b-tshirts_18',
    'prod_kt-b-tshirts_2', 'prod_kt-b-tshirts_20', 'prod_kt-b-tshirts_24', 'prod_kt-b-tshirts_26',
    'prod_kt-b-tshirts_30'
  ],
  newArrivalsCount: 29,
  dealsEnabled: true,
  dealsTitle: 'FESTIVE BLOWOUT DEALS 🔥',
  dealsTimerHours: 18,
  dealsTimerMinutes: 30,
  dealsMinDiscount: 25,
  newArrivalsEnabled: true,
  newArrivalsTitle: 'CELEBRITY ETHNIC COUTURE ✨',
  newArrivalsSubtitle: 'Handwoven silk sarees and designer kurtas direct from Master Weavers',
  newArrivalsBadge: 'FESTIVE 2026',
  newArrivalsMaxItems: 12
};

interface StoreContextType {
  settings: SiteSettings;
  updateSettings: (newSettings: Partial<SiteSettings>) => Promise<void>;
  productDisplay: ProductDisplay;
  updateProductDisplay: (newDisplay: Partial<ProductDisplay>) => Promise<void>;
  categories: Category[];
  setCategories: React.Dispatch<React.SetStateAction<Category[]>>;
  brands: Brand[];
  setBrands: React.Dispatch<React.SetStateAction<Brand[]>>;
  saveBrand: (brand: Brand) => Promise<void>;
  toggleBrand: (id: string) => Promise<void>;
  deleteBrand: (id: string) => Promise<void>;
  products: Product[];
  setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  banners: Banner[];
  setBanners: React.Dispatch<React.SetStateAction<Banner[]>>;
  isBannersLoaded: boolean;
  saveBanner: (banner: Banner) => Promise<void>;
  toggleBanner: (id: string) => Promise<void>;
  deleteBanner: (id: string) => Promise<void>;
  coupons: Coupon[];
  setCoupons: React.Dispatch<React.SetStateAction<Coupon[]>>;
  saveCoupon: (coupon: Coupon) => Promise<void>;
  toggleCoupon: (id: string) => Promise<void>;
  deleteCoupon: (id: string) => Promise<void>;
  cart: CartItem[];
  addToCart: (product: Product, variant: ProductVariant, qty?: number) => void;
  removeFromCart: (cartItemId: string) => void;
  updateCartQty: (cartItemId: string, qty: number) => void;
  clearCart: () => void;
  appliedCoupon: Coupon | null;
  couponDiscount: number;
  applyCoupon: (code: string) => Promise<{ success: boolean; message: string }>;
  removeCoupon: () => void;
  wishlist: string[];
  toggleWishlist: (productId: string) => void;
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  logoutUser: () => void;
  loginUser: (name: string, email: string, phone?: string) => void;
  isAdminLoggedIn: boolean;
  adminLogin: (password: string) => Promise<{ success: boolean; error?: string }>;
  adminLogout: () => void;
  saveSettings: (newSettings: Partial<SiteSettings>) => Promise<void>;
  createProduct: (productData: Partial<Product>) => Promise<void>;
  updateProduct: (id: string, productData: Partial<Product>) => Promise<void>;
  updateProductStock: (productId: string, newStock: number) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  orders: Order[];
  createOrder: (orderData: Partial<Order>) => Promise<Order | null>;
  updateOrderStatus: (orderId: string, status: Order['status'], trackingNum?: string, orderObj?: Order, invoiceNum?: string) => Promise<void>;
  setOrderInvoiceNumber: (orderId: string, invoiceNumber: string) => Promise<void>;
  requestOrderReturn: (orderId: string, returnType: 'return' | 'exchange', reason: string, details?: { exchangeSize?: string; exchangeColor?: string; comments?: string }) => void;
  submitReturnRequest?: (orderId: string, returnType: 'return' | 'exchange', reason: string, selectedIds: string[], details?: { exchangeSize?: string; exchangeColor?: string; comments?: string }) => Promise<void>;
  decideOrderReturn: (orderId: string, decisions: Array<{ itemId: string; decision: 'accepted' | 'rejected' }>) => Promise<void>;
  updateReturnStatus: (orderId: string, returnStatus: 'approved' | 'rejected' | 'completed', adminNotes?: string) => void;
  reviews: Review[];
  setReviews: React.Dispatch<React.SetStateAction<Review[]>>;
  addReview: (reviewData: Partial<Review>) => Promise<Review>;
  updateReview: (id: string, reviewData: Partial<Review>) => Promise<Review | null>;
  updateReviewStatus: (id: string, status: 'approved' | 'pending' | 'rejected') => Promise<void>;
  deleteReview: (id: string) => Promise<void>;
  refreshReviews: () => Promise<Review[] | void>;
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  resetFilters: () => void;
  toastMessage: string | null;
  showToast: (msg: string) => void;
  searchModalOpen: boolean;
  setSearchModalOpen: (open: boolean) => void;
  cartDrawerOpen: boolean;
  setCartDrawerOpen: (open: boolean) => void;
  sizeChartCategory: string | null;
  setSizeChartCategory: (catId: string | null) => void;
  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;
  reloadCatalog: () => Promise<void>;
  blogPosts: BlogPost[];
  setBlogPosts: React.Dispatch<React.SetStateAction<BlogPost[]>>;
  saveBlogPost: (postData: Partial<BlogPost> & { id?: string }) => Promise<BlogPost | null>;
  deleteBlogPost: (id: string) => Promise<boolean>;
  toggleBlogPostStatus: (id: string) => Promise<void>;
  reloadBlogPosts: () => Promise<void>;
}

const defaultFilters: FilterState = {
  types: [],
  occasions: [],
  sizes: [],
  colors: [],
  fabrics: [],
  fits: [],
  tags: [],
  minPrice: 0,
  maxPrice: 10000,
  minDiscount: 0,
  rating: 0,
  plusSizeOnly: false,
  searchQuery: '',
  sortBy: 'popularity'
};

function parseDbBlogPost(p: any): BlogPost {
  let content = p.content;
  if (typeof content === 'string') {
    try { content = JSON.parse(content); } catch (e) { content = [content]; }
  }
  let tags = p.tags;
  if (typeof tags === 'string') {
    try { tags = JSON.parse(tags); } catch (e) { tags = []; }
  }
  return {
    ...p,
    content: Array.isArray(content) ? content : [String(content || '')],
    tags: Array.isArray(tags) ? tags : [],
    isPublished: p.isPublished !== false,
    status: p.isPublished !== false ? 'published' : 'draft',
    createdAt: p.createdAt ? new Date(p.createdAt).toISOString() : new Date().toISOString(),
    updatedAt: p.updatedAt ? new Date(p.updatedAt).toISOString() : new Date().toISOString()
  };
}

function normalizeCategories(rawList: any[]): Category[] {
  if (!Array.isArray(rawList)) return [];
  return rawList.map((cat: any) => {
    let subcategories = cat.subcategories;
    if (typeof subcategories === 'string') {
      try { subcategories = JSON.parse(subcategories); } catch { subcategories = []; }
    }
    if (!Array.isArray(subcategories)) subcategories = [];

    const normalizedSubs = subcategories.map((sub: any) => {
      let types = sub.types;
      if (typeof types === 'string') {
        try { types = JSON.parse(types); } catch { types = []; }
      }
      if (!Array.isArray(types)) types = [];
      return {
        ...sub,
        types
      };
    });

    return {
      ...cat,
      subcategories: normalizedSubs
    };
  });
}

export function normalizeBanner(b: any): Banner {
  if (!b) return b;
  let image = sanitizeImageUrl(b.image || b.imageUrl || b.mobileImage || b.mobileImageUrl || '');
  let mobileImage = sanitizeImageUrl(b.mobileImage || b.mobileImageUrl || b.image || b.imageUrl || '', image);
  const link = b.link || b.linkUrl || '/';
  const position = b.position || b.type || b.placement || 'hero';
  const sortOrder = typeof b.sortOrder === 'number' ? b.sortOrder : (typeof b.displayOrder === 'number' ? b.displayOrder : parseInt(b.sortOrder || b.displayOrder || '1', 10) || 1);
  const isActive = b.isActive !== false && b.status !== 'inactive';
  return {
    id: String(b.id || `ban-${Date.now()}`),
    title: b.title || 'PGmart Promotion',
    subtitle: b.subtitle || undefined,
    image,
    mobileImage: mobileImage || image,
    link,
    buttonText: b.buttonText || 'EXPLORE COLLECTION',
    position: position as 'hero' | 'category' | 'promo_strip' | 'ad_banner',
    sortOrder,
    isActive
  };
}

export function normalizeBanners(list: any[]): Banner[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeBanner).filter(Boolean);
}

export const BRAND_LOGO_MAP: Record<string, string> = {
  'manyavar': '/images/brands/manyavar.svg',
  'manyavar-royal': '/images/brands/manyavar.svg',
  'raymond': '/images/brands/raymond.svg',
  'peter-england': '/images/brands/peter-england.svg',
  'van-heusen': '/images/brands/van-heusen.svg',
  'jockey': '/images/brands/jockey.svg',
  'us-polo-assn': '/images/brands/us-polo-assn.svg',
  'u-s-polo-assn': '/images/brands/us-polo-assn.svg',
  'pepe-jeans': '/images/brands/pepe-jeans.svg',
  'mufti': '/images/brands/mufti.svg',
  'monte-carlo': '/images/brands/monte-carlo.svg',
  'biba': '/images/brands/biba.svg',
  'w-for-woman': '/images/brands/w-for-woman.svg',
  'fabindia': '/images/brands/fabindia.svg',
  'aurelia': '/images/brands/aurelia.svg',
  'allen-solly': '/images/brands/allen-solly.svg',
  'blackberrys': '/images/brands/blackberrys.svg',
  'levis': '/images/brands/levis.svg',
  'mohey': '/images/brands/mohey.svg',
  'neerus': '/images/brands/neerus.svg',
  'sparky-jeans': '/images/brands/sparky-jeans.svg',
  'catmint': '/images/brands/catmint.svg',
  'flu-jeans': '/images/brands/flu-jeans.svg',
  'bad-boyz': '/images/brands/bad-boyz.svg',
  'lolipoli': '/images/brands/lolipoli.svg',
  'tulip': '/images/brands/tulip.svg',
  'pgmart': '/images/brands/pgmart.svg',
  'craftsvilla': '/images/brands/craftsvilla.svg',
  'sabhyata': '/images/brands/sabhyata.svg',
  'zara-elite': '/images/brands/zara-elite.svg',
  'saffron': '/images/brands/saffron.svg'
};

export function normalizeBrand(b: any): Brand {
  if (!b) return b;
  const name = String(b.name || 'Brand');
  const slug = String(b.slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''));
  let logo = b.logo || b.logoUrl || '';

  // If logo is a generic unsplash placeholder, data placeholder or empty, map to official brand SVG logo
  if (!logo || logo.includes('unsplash.com') || logo.includes('test_brand') || logo.includes('photo-15')) {
    if (BRAND_LOGO_MAP[slug]) {
      logo = BRAND_LOGO_MAP[slug];
    } else {
      const matchedKey = Object.keys(BRAND_LOGO_MAP).find(k => slug.includes(k) || k.includes(slug) || name.toLowerCase().includes(k));
      logo = matchedKey ? BRAND_LOGO_MAP[matchedKey] : '/images/brands/pgmart.svg';
    }
  }

  return {
    id: String(b.id || `b-${Date.now()}`),
    name,
    slug,
    logo,
    description: b.description || 'Official Brand Partner',
    isFeatured: b.isFeatured !== false && b.featured !== false,
    isActive: b.isActive !== false && b.status !== 'inactive'
  };
}

export function normalizeBrands(list: any[]): Brand[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeBrand).filter(Boolean);
}

export function normalizeReview(r: any): Review {
  if (!r) return r;
  const status = (r.status || 'approved') as 'approved' | 'pending' | 'rejected';
  return {
    id: String(r.id || `rev-${Date.now()}`),
    productId: String(r.productId || ''),
    customerName: String(r.customerName || r.userName || r.name || 'Valued Customer'),
    rating: Number(r.rating) || 5,
    title: r.title ? String(r.title) : undefined,
    comment: String(r.comment || ''),
    photos: Array.isArray(r.photos) ? r.photos : [],
    isVerifiedPurchase: r.isVerifiedPurchase !== false && r.verified !== false,
    status,
    createdAt: r.createdAt ? new Date(r.createdAt).toISOString() : new Date().toISOString()
  };
}

export function normalizeReviews(list: any[]): Review[] {
  if (!Array.isArray(list)) return [];
  return list.map(normalizeReview).filter(Boolean);
}

export function normalizeProduct(p: any): Product {
  if (!p) return p;
  const isNonReturnable = Boolean(p.isNonReturnable || p.noReturn);
  const stock = typeof p.stock === 'number' ? p.stock : (p.inStock === false ? 0 : 50);

  // Normalize Images
  let rawImages: string[] = [];
  if (Array.isArray(p.images) && p.images.length > 0) {
    rawImages = p.images;
  } else if (typeof p.images === 'string' && p.images.trim()) {
    try {
      const parsed = JSON.parse(p.images);
      if (Array.isArray(parsed)) rawImages = parsed;
      else rawImages = p.images.split(',').map((s: string) => s.trim());
    } catch {
      rawImages = p.images.split(',').map((s: string) => s.trim());
    }
  }
  const cleanImages = rawImages
    .filter(img => typeof img === 'string' && img.trim() !== '')
    .map(img => sanitizeImageUrl(img, ''))
    .filter(Boolean);

  let rawSizes: any[] = [];
  if (Array.isArray(p.availableSizes)) {
    rawSizes = p.availableSizes;
  } else if (typeof p.availableSizes === 'string') {
    try {
      const parsed = JSON.parse(p.availableSizes);
      if (Array.isArray(parsed)) rawSizes = parsed;
      else rawSizes = p.availableSizes.split(',').map((s: string) => s.trim());
    } catch {
      rawSizes = p.availableSizes.split(',').map((s: string) => s.trim());
    }
  }
  const cleanSizes = rawSizes
    .map(s => (typeof s === 'string' ? s.trim() : (s ? String(s).trim() : '')))
    .filter(s => s && !s.toLowerCase().includes('undefined') && s.toLowerCase() !== 'null');

  const rawColors = Array.isArray(p.colors) ? p.colors : (typeof p.colors === 'string' ? JSON.parse(p.colors || '[]') : []);
  const sanitizedColors = (rawColors || []).map((c: any) => ({
    ...c,
    images: Array.isArray(c?.images) ? c.images.map((img: string) => sanitizeImageUrl(img, '')).filter(Boolean) : []
  }));

  const rawVariants = Array.isArray(p.variants) ? p.variants : (typeof p.variants === 'string' ? JSON.parse(p.variants || '[]') : []);
  const sanitizedVariants = (rawVariants || []).map((v: any) => ({
    ...v,
    image: v?.image ? sanitizeImageUrl(v.image, '') : undefined,
    images: Array.isArray(v?.images) ? v.images.map((img: string) => sanitizeImageUrl(img, '')).filter(Boolean) : []
  }));

  return {
    ...p,
    stock,
    isNonReturnable,
    noReturn: isNonReturnable,
    images: cleanImages,
    tags: Array.isArray(p.tags) ? p.tags : (typeof p.tags === 'string' ? JSON.parse(p.tags || '[]') : []),
    variants: sanitizedVariants,
    colors: sanitizedColors,
    availableSizes: cleanSizes.length > 0 ? cleanSizes : ['Free Size']
  };
}

// Safe localStorage helper with automatic QuotaExceededError recovery & cache trimming
const safeStorageSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch (e: any) {
    if (e?.name === 'QuotaExceededError' || e?.code === 22 || e?.code === 1014 || (typeof e?.message === 'string' && e.message.includes('quota'))) {
      try {
        const legacyKeys = [
          'terra_banners_v1', 'terra_banners_v2', 'terra_banners_v3', 'terra_banners_v4', 'terra_banners_v5',
          'terra_banners_v6', 'terra_banners_v7', 'terra_banners_v8', 'terra_products', 'terra_settings',
          'terra_orders', 'terra_wishlist_v1', 'pgmart_analytics_v1', 'pgmart_marketing_data'
        ];
        legacyKeys.forEach(k => { try { localStorage.removeItem(k); } catch {} });
        localStorage.setItem(key, value);
      } catch {
        // Degrade safely without throwing unhandled exceptions
      }
    }
  }
};

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export const StoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SiteSettings>(() => {
    try {
      const saved = localStorage.getItem('pgmart_site_settings');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (!parsed.razorpayKeyId || parsed.razorpayKeyId.includes('123456789') || parsed.razorpayKeyId.startsWith('rzp_test_')) {
          parsed.razorpayKeyId = 'rzp_live_TQsQotOvsIaMs4';
          safeStorageSet('pgmart_site_settings', JSON.stringify({ ...initialSiteSettings, ...parsed }));
        }
        return { ...initialSiteSettings, ...parsed };
      }
    } catch {}
    return initialSiteSettings;
  });

  const [productDisplay, setProductDisplay] = useState<ProductDisplay>(() => {
    try {
      const saved = localStorage.getItem('pgmart_product_display');
      if (saved) return JSON.parse(saved);
    } catch {}
    return initialProductDisplay;
  });
  const [categories, setCategoriesState] = useState<Category[]>(() => {
    try {
      const saved = localStorage.getItem('pgmart_categories_v2');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return normalizeCategories(parsed);
      }
    } catch {}
    return normalizeCategories(initialCategories);
  });

  const setCategories = (cats: Category[] | ((prev: Category[]) => Category[])) => {
    setCategoriesState(prev => {
      const next = typeof cats === 'function' ? cats(prev) : cats;
      const normalized = normalizeCategories(next);
      safeStorageSet('pgmart_categories_v2', JSON.stringify(normalized));
      return normalized;
    });
  };

  const [brands, setBrands] = useState<Brand[]>(() => {
    try {
      const saved = localStorage.getItem('pgmart_brands');
      if (saved) return normalizeBrands(JSON.parse(saved));
    } catch {}
    return initialBrands;
  });

  const [products, setProductsState] = useState<Product[]>(() => {
    try {
      const saved = localStorage.getItem('pgmart_products_v2');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed.map(normalizeProduct);
      }
    } catch {}
    return initialProducts.map(normalizeProduct);
  });

  const setProducts = (prods: Product[] | ((prev: Product[]) => Product[])) => {
    setProductsState(prev => {
      const next = typeof prods === 'function' ? prods(prev) : prods;
      const normalized = Array.isArray(next) ? next.map(normalizeProduct) : next;
      safeStorageSet('pgmart_products_v2', JSON.stringify(normalized));
      return normalized;
    });
  };
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(() => {
    try {
      const saved = localStorage.getItem('pgmart_blog_posts_v2');
      if (saved) return JSON.parse(saved);
    } catch {}
    return initialBlogPosts;
  });
  const [isBannersLoaded, setIsBannersLoaded] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('pgmart_live_banners_v1');
      if (saved) {
        const parsed = JSON.parse(saved);
        return Array.isArray(parsed) && parsed.length > 0;
      }
    } catch {}
    return false;
  });

  const [banners, setBanners] = useState<Banner[]>(() => {
    try {
      const saved = localStorage.getItem('pgmart_live_banners_v1');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return normalizeBanners(parsed);
        }
      }
    } catch {}
    return [];
  });

  // Fetch live database records on mount (Cache-busted for Mobile, Tablet & Desktop)
  useEffect(() => {
    let isMounted = true;

    async function bootstrapStoreData() {
      const cacheBustUrl = `/api/bootstrap?_t=${Date.now()}`;
      try {
        const res = await fetch(cacheBustUrl, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
        if (res.ok) {
          const text = await res.text();
          if (text.startsWith('{')) {
            const data = JSON.parse(text);
            if (!isMounted) return;

            if (Array.isArray(data.banners)) {
              const normalized = normalizeBanners(data.banners);
              setBanners(normalized);
              safeStorageSet('pgmart_live_banners_v1', JSON.stringify(normalized));
              setIsBannersLoaded(true);
            }
            if (data.settings && typeof data.settings === 'object') {
              setSettings(prev => {
                const merged = { ...prev, ...data.settings };
                safeStorageSet('pgmart_site_settings', JSON.stringify(merged));
                return merged;
              });
            }
            if (data.productDisplay && typeof data.productDisplay === 'object') {
              setProductDisplay(prev => {
                const merged = { ...prev, ...data.productDisplay };
                safeStorageSet('pgmart_product_display', JSON.stringify(merged));
                return merged;
              });
            }
            if (Array.isArray(data.categories) && data.categories.length > 0) {
              const normalized = normalizeCategories(data.categories);
              setCategoriesState(normalized);
              safeStorageSet('pgmart_categories_v2', JSON.stringify(normalized));
            }
            if (Array.isArray(data.products) && data.products.length > 0) {
              const normalized = data.products.map(normalizeProduct);
              setProductsState(normalized);
              safeStorageSet('pgmart_products_v2', JSON.stringify(normalized));
            }
            if (Array.isArray(data.brands) && data.brands.length > 0) {
              const normalized = normalizeBrands(data.brands);
              setBrands(normalized);
              safeStorageSet('pgmart_brands', JSON.stringify(normalized));
            }
            if (Array.isArray(data.coupons) && data.coupons.length > 0) {
              setCoupons(data.coupons);
              safeStorageSet('pgmart_coupons', JSON.stringify(data.coupons));
            }
            if (Array.isArray(data.reviews) && data.reviews.length > 0) {
              const normalized = normalizeReviews(data.reviews);
              setReviews(normalized);
              safeStorageSet('pgmart_reviews_v2', JSON.stringify(normalized));
            }
            return;
          }
        }
      } catch (e) {
        console.warn('[Bootstrap sync warning]:', e);
      } finally {
        if (isMounted) setIsBannersLoaded(true);
      }

      // Fallback individual banner load if /api/bootstrap was not available
      try {
        const bannerRes = await fetch(`/api/banners?_t=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        if (bannerRes.ok) {
          const bannerText = await bannerRes.text();
          if (bannerText.startsWith('{') || bannerText.startsWith('[')) {
            const bannerData = JSON.parse(bannerText);
            if (Array.isArray(bannerData) && isMounted) {
              const normalized = normalizeBanners(bannerData);
              setBanners(normalized);
              safeStorageSet('pgmart_live_banners_v1', JSON.stringify(normalized));
              setIsBannersLoaded(true);
            }
          }
        }
      } catch (e) {
        console.warn('[Fallback banners sync warning]:', e);
      } finally {
        if (isMounted) setIsBannersLoaded(true);
      }
    }

    bootstrapStoreData();

    return () => {
      isMounted = false;
    };
  }, []);
  const [coupons, setCoupons] = useState<Coupon[]>(() => {
    try {
      const saved = localStorage.getItem('pgmart_coupons');
      if (saved) return JSON.parse(saved);
    } catch {}
    return initialCoupons;
  });
  const [orders, setOrders] = useState<Order[]>(() => {
    try {
      const saved = localStorage.getItem('terra_orders_v2');
      if (saved) return JSON.parse(saved);
    } catch {}
    return [];
  });
  const [reviews, setReviews] = useState<Review[]>(() => {
    try {
      const saved = localStorage.getItem('pgmart_reviews_v2');
      if (saved) return normalizeReviews(JSON.parse(saved));
      const defaultReviews: Review[] = [
        {
          id: 'rev-1',
          productId: 'w-1',
          customerName: 'Aishwarya Roy',
          rating: 5,
          title: 'Breathtaking quality & authentic weave!',
          comment: 'Absolutely gorgeous Banarasi saree! The gold zari work is extremely fine and matches the photo perfectly. Delivery was super fast within Kolkata.',
          isVerifiedPurchase: true,
          status: 'approved',
          createdAt: '2026-08-01T14:30:00Z'
        },
        {
          id: 'rev-2',
          productId: 'm-1',
          customerName: 'Vikram Seth',
          rating: 5,
          title: 'Pure handloom feel, perfect fitting',
          comment: 'The terracotta kurta set has top-notch stitching and pure breathable fabric. Got tons of compliments at the festive gathering!',
          isVerifiedPurchase: true,
          status: 'approved',
          createdAt: '2026-07-29T10:15:00Z'
        },
        {
          id: 'rev-3',
          productId: 'k-1',
          customerName: 'Priyanka Ghosh',
          rating: 5,
          title: 'Kids clothing is so soft and gentle',
          comment: 'Super soft material for my 4 year old boy. No irritation on skin and the embroidery is gentle on the inside lining.',
          isVerifiedPurchase: true,
          status: 'approved',
          createdAt: '2026-07-26T16:45:00Z'
        },
        {
          id: 'rev-4',
          productId: 'u-1',
          customerName: 'Debarati Mukherjee',
          rating: 5,
          title: 'Ultimate luxury innerwear comfort',
          comment: 'Very premium organic cotton fabric. Durable elastic, seamless finish, and keeps you comfortable all day long.',
          isVerifiedPurchase: true,
          status: 'approved',
          createdAt: '2026-07-22T11:20:00Z'
        },
        {
          id: 'rev-5',
          productId: 'w-2',
          customerName: 'Sneha Banerjee',
          rating: 5,
          title: 'Loved the packaging and weaver story!',
          comment: 'Authentic Indian ethnic fashion at such fair direct pricing. Very happy to support artisan weavers through PGmart.',
          isVerifiedPurchase: true,
          status: 'approved',
          createdAt: '2026-07-18T18:00:00Z'
        }
      ];
      safeStorageSet('pgmart_reviews_v2', JSON.stringify(defaultReviews));
      return defaultReviews;
    } catch {
      return [];
    }
  });

  // Fetch reviews from Database REST API on mount
  useEffect(() => {
    async function loadDbReviews() {
      try {
        const res = await fetch('/api/reviews');
        if (res.ok) {
          const text = await res.text();
          if (text.startsWith('{') || text.startsWith('[')) {
            const data = JSON.parse(text);
            if (Array.isArray(data) && data.length > 0) {
              const normalized = normalizeReviews(data);
              setReviews(prev => {
                const map = new Map<string, Review>();
                normalized.forEach(r => map.set(r.id, r));
                prev.forEach(r => {
                  if (!map.has(r.id)) map.set(r.id, r);
                });
                const merged = Array.from(map.values());
                safeStorageSet('pgmart_reviews_v2', JSON.stringify(merged));
                return merged;
              });
              return;
            }
          }
        }
      } catch (e) {
        console.warn('REST API /api/reviews fetch warning:', e);
      }
    }
    loadDbReviews();
  }, []);

  // Helper to normalize and parse DB blog post
  const parseDbBlogPost = (p: any): BlogPost => {
    if (!p) return p;
    let content: string[] = [];
    if (Array.isArray(p.content)) {
      content = p.content;
    } else if (typeof p.content === 'string') {
      try {
        const parsed = JSON.parse(p.content);
        content = Array.isArray(parsed) ? parsed : [p.content];
      } catch {
        content = p.content.includes('\n\n') ? p.content.split('\n\n') : [p.content];
      }
    }

    let tags: string[] = [];
    if (Array.isArray(p.tags)) {
      tags = p.tags;
    } else if (typeof p.tags === 'string') {
      try {
        const parsed = JSON.parse(p.tags);
        tags = Array.isArray(parsed) ? parsed : p.tags.split(',').map((t: string) => t.trim());
      } catch {
        tags = p.tags.split(',').map((t: string) => t.trim());
      }
    }

    const isPublished = p.isPublished !== false && p.status !== 'draft';

    return {
      id: p.id,
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt || '',
      content,
      category: p.category || 'Styling Tips',
      author: p.author || 'Priyam Ghoshal',
      authorRole: p.authorRole || 'Founder & CEO, PGmart',
      authorAvatar: p.authorAvatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80',
      publishedDate: p.publishedDate || formatDateIST(new Date()),
      readTime: p.readTime || '5 min read',
      featuredImage: p.featuredImage || 'https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1200&q=80',
      relatedCategorySlug: p.relatedCategorySlug || 'women',
      tags,
      isPublished,
      status: isPublished ? 'published' : 'draft',
      metaTitle: p.metaTitle || p.title,
      metaDesc: p.metaDesc || p.excerpt || '',
      createdAt: p.createdAt,
      updatedAt: p.updatedAt
    };
  };

  // Fetch blog posts from Database REST API on mount
  useEffect(() => {
    async function loadDbBlogPosts() {
      const postMap = new Map<string, BlogPost>();

      try {
        const res = await fetch('/api/blogs');
        if (res.ok) {
          const text = await res.text();
          if (text.startsWith('{') || text.startsWith('[')) {
            const data = JSON.parse(text);
            if (Array.isArray(data)) {
              data.forEach(p => {
                if (p && p.id) postMap.set(p.id, parseDbBlogPost(p));
              });
            }
          }
        }
      } catch (e) {
        console.warn('REST API /api/blogs fetch warning:', e);
      }

      if (postMap.size > 0) {
        const list = Array.from(postMap.values());
        setBlogPosts(list);
        safeStorageSet('pgmart_blog_posts_v2', JSON.stringify(list));
      } else {
        setBlogPosts(initialBlogPosts);
        safeStorageSet('pgmart_blog_posts_v2', JSON.stringify(initialBlogPosts));
      }
    }
    loadDbBlogPosts();
  }, []);

  const saveBlogPost = async (postData: Partial<BlogPost> & { id?: string }): Promise<BlogPost | null> => {
    const postId = postData.id || `post-${Date.now()}`;
    const slug = postData.slug || postData.title?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `post-${Date.now()}`;
    const nowIso = new Date().toISOString();

    const formattedPost: BlogPost = {
      id: postId,
      slug,
      title: postData.title || 'Untitled Post',
      excerpt: postData.excerpt || '',
      content: Array.isArray(postData.content) ? postData.content : [String(postData.content || '')],
      category: postData.category || 'Styling Tips',
      author: postData.author || 'Priyam Ghoshal',
      authorRole: postData.authorRole || 'Founder & CEO, PGmart',
      authorAvatar: postData.authorAvatar || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=200&q=80',
      publishedDate: postData.publishedDate || formatDateIST(new Date()),
      readTime: postData.readTime || '5 min read',
      featuredImage: postData.featuredImage || 'https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1200&q=80',
      relatedCategorySlug: postData.relatedCategorySlug || 'women',
      tags: Array.isArray(postData.tags) ? postData.tags : [],
      isPublished: postData.isPublished !== false && postData.status !== 'draft',
      status: (postData.isPublished !== false && postData.status !== 'draft') ? 'published' : 'draft',
      metaTitle: postData.metaTitle || postData.title || null as any,
      metaDesc: postData.metaDesc || postData.excerpt || null as any,
      createdAt: postData.createdAt || nowIso,
      updatedAt: nowIso
    };

    // 1. Update state and localStorage immediately
    setBlogPosts(prev => {
      const exists = prev.some(p => p.id === postId || p.slug === slug);
      const nextList = exists
        ? prev.map(p => (p.id === postId || p.slug === slug) ? formattedPost : p)
        : [formattedPost, ...prev];
      safeStorageSet('pgmart_blog_posts_v2', JSON.stringify(nextList));
      return nextList;
    });

    showToast(`Blog Post "${formattedPost.title}" saved successfully.`);

    // 2. REST API MySQL backend sync
    try {
      const isExisting = postData.id && blogPosts.some(p => p.id === postData.id);
      const res = await adminFetch(isExisting ? `/api/blogs/${postData.id}` : '/api/blogs', {
        method: isExisting ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formattedPost)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.post) {
          const apiPost = parseDbBlogPost(data.post);
          setBlogPosts(prev => {
            const next = prev.map(p => (p.id === postId || p.slug === slug) ? apiPost : p);
            safeStorageSet('pgmart_blog_posts_v2', JSON.stringify(next));
            return next;
          });
          return apiPost;
        }
      }
    } catch (e) {
      console.warn('[REST API saveBlogPost error]:', e);
    }

    return formattedPost;
  };

  const deleteBlogPost = async (id: string): Promise<boolean> => {
    // 1. Update state and localStorage immediately
    setBlogPosts(prev => {
      const nextList = prev.filter(p => p.id !== id && p.slug !== id);
      safeStorageSet('pgmart_blog_posts_v2', JSON.stringify(nextList));
      return nextList;
    });

    showToast('Blog article deleted.');

    // 2. REST API delete
    try {
      await adminFetch(`/api/blogs/${id}`, { method: 'DELETE' });
    } catch (e) {
      console.warn('[REST API deleteBlogPost error]:', e);
    }

    return true;
  };

  const toggleBlogPostStatus = async (id: string): Promise<void> => {
    const post = blogPosts.find(p => p.id === id || p.slug === id);
    if (!post) return;
    const nextPublished = !post.isPublished;
    const nextStatus = nextPublished ? 'published' : 'draft';
    const nowIso = new Date().toISOString();

    setBlogPosts(prev => {
      const nextList = prev.map(p => (p.id === id || p.slug === id) ? { ...p, isPublished: nextPublished, status: nextStatus, updatedAt: nowIso } : p);
      safeStorageSet('pgmart_blog_posts_v2', JSON.stringify(nextList));
      return nextList;
    });

    showToast(`Blog status set to ${nextPublished ? 'PUBLISHED' : 'DRAFT'}.`);

    try {
      await adminFetch(`/api/blogs/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPublished: nextPublished, status: nextStatus })
      });
    } catch (e) {
      console.warn('[REST API toggleBlogPostStatus error]:', e);
    }
  };

  const reloadBlogPosts = async (): Promise<void> => {
    try {
      const res = await fetch('/api/blogs');
      if (res.ok) {
        const text = await res.text();
        if (text.startsWith('{') || text.startsWith('[')) {
          const data = JSON.parse(text);
          if (Array.isArray(data)) {
            const list = data.map(parseDbBlogPost);
            setBlogPosts(list);
            safeStorageSet('pgmart_blog_posts_v2', JSON.stringify(list));
          }
        }
      }
    } catch (e) {
      console.warn('[reloadBlogPosts error]:', e);
    }
  };

  // Recalculate product rating and review count in memory when reviews change
  const recalculateProductReviewsInState = (productId: string, updatedReviews: Review[]) => {
    if (!productId) return;
    const targetProdId = productId.toLowerCase();
    const approvedReviews = updatedReviews.filter(
      r => (r.productId && (r.productId.toLowerCase() === targetProdId || r.productId === productId)) && r.status === 'approved'
    );
    const count = approvedReviews.length;
    const avgRating = count > 0 
      ? Number((approvedReviews.reduce((sum, r) => sum + (Number(r.rating) || 5), 0) / count).toFixed(1))
      : 4.5;

    setProducts(prevProds => {
      const nextProds = prevProds.map(p => {
        if (p.id === productId || p.slug === productId || (p.id && p.id.toLowerCase() === targetProdId) || (p.slug && p.slug.toLowerCase() === targetProdId)) {
          return { ...p, rating: avgRating, reviewCount: count };
        }
        return p;
      });
      safeStorageSet('pgmart_products_v2', JSON.stringify(nextProds));
      return nextProds;
    });
  };

  const refreshReviews = async (): Promise<Review[]> => {
    try {
      const res = await fetch(`/api/reviews?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      if (res.ok) {
        const text = await res.text();
        if (text.startsWith('{') || text.startsWith('[')) {
          const data = JSON.parse(text);
          if (Array.isArray(data)) {
            const normalized = normalizeReviews(data);
            setReviews(normalized);
            safeStorageSet('pgmart_reviews_v2', JSON.stringify(normalized));
            return normalized;
          }
        }
      }
    } catch (e) {
      console.warn('refreshReviews fetch warning:', e);
    }
    return [];
  };

  const addReview = async (reviewData: Partial<Review>) => {
    const reviewId = reviewData.id || `rev-${Date.now()}`;
    const targetProductId = reviewData.productId || 'w-1';
    const payload = {
      id: reviewId,
      productId: targetProductId,
      customerName: reviewData.customerName || 'PGmart Shopper',
      rating: Number(reviewData.rating) || 5,
      title: reviewData.title || `${reviewData.rating || 5} Star Experience`,
      comment: reviewData.comment || '',
      photos: Array.isArray(reviewData.photos) ? JSON.stringify(reviewData.photos) : (reviewData.photos || '[]'),
      isVerifiedPurchase: reviewData.isVerifiedPurchase !== false,
      status: reviewData.status || 'pending',
      createdAt: new Date().toISOString()
    };

    let savedReview: Review | null = null;

    // Database REST API Sync (MySQL Backend)
    try {
      const res = await fetch('/api/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.review) {
          savedReview = data.review;
        }
      }
    } catch (apiErr) {
      console.warn('[API Review sync warning]:', apiErr);
    }

    const finalReview: Review = savedReview || {
      ...payload,
      photos: typeof payload.photos === 'string' ? JSON.parse(payload.photos || '[]') : payload.photos
    } as any;

    setReviews(prev => {
      const updated = [finalReview, ...prev.filter(r => r.id !== finalReview.id)];
      safeStorageSet('pgmart_reviews_v2', JSON.stringify(updated));
      recalculateProductReviewsInState(targetProductId, updated);
      return updated;
    });

    return finalReview;
  };

  const updateReview = async (id: string, reviewData: Partial<Review>) => {
    let updatedReviewObj: Review | null = null;
    let targetProductId = reviewData.productId || '';

    setReviews(prev => {
      const existing = prev.find(r => r.id === id);
      if (existing && !targetProductId) {
        targetProductId = existing.productId;
      }
      const updated = prev.map(r => {
        if (r.id === id) {
          const merged: Review = { ...r, ...reviewData };
          updatedReviewObj = merged;
          return merged;
        }
        return r;
      });
      safeStorageSet('pgmart_reviews_v2', JSON.stringify(updated));
      if (targetProductId) {
        recalculateProductReviewsInState(targetProductId, updated);
      }
      return updated;
    });

    showToast('Review updated successfully.');

    // Database REST API Update (MySQL Backend)
    try {
      const res = await adminFetch(`/api/reviews/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewData)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.review) {
          updatedReviewObj = data.review;
        }
      }
    } catch (e) {
      console.warn('Backend sync for review update failed', e);
    }

    return updatedReviewObj;
  };

  const updateReviewStatus = async (id: string, status: 'approved' | 'pending' | 'rejected') => {
    let targetProductId = '';
    setReviews(prev => {
      const existing = prev.find(r => r.id === id);
      if (existing) targetProductId = existing.productId;
      const updated = prev.map(r => r.id === id ? { ...r, status } : r);
      safeStorageSet('pgmart_reviews_v2', JSON.stringify(updated));
      if (targetProductId) {
        recalculateProductReviewsInState(targetProductId, updated);
      }
      return updated;
    });
    showToast(`Review status updated to ${status.toUpperCase()}`);

    // Database REST API Update (MySQL Backend)
    try {
      await adminFetch(`/api/reviews/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
    } catch (e) {
      console.warn('Backend sync for review status update failed', e);
    }
  };

  const deleteReview = async (id: string) => {
    let targetProductId = '';
    setReviews(prev => {
      const existing = prev.find(r => r.id === id);
      if (existing) targetProductId = existing.productId;
      const updated = prev.filter(r => r.id !== id);
      safeStorageSet('pgmart_reviews_v2', JSON.stringify(updated));
      if (targetProductId) {
        recalculateProductReviewsInState(targetProductId, updated);
      }
      return updated;
    });
    showToast('Review removed.');

    // Database REST API Delete (MySQL Backend)
    try {
      await adminFetch(`/api/reviews/${id}`, {
        method: 'DELETE'
      });
    } catch (e) {
      console.warn('Backend sync for review deletion failed', e);
    }
  };
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('terra_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [wishlist, setWishlist] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('terra_wishlist');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [couponDiscount, setCouponDiscount] = useState<number>(0);
  const [user, setUser] = useState<User | null>(() => {
    try {
      const saved = localStorage.getItem('terra_user');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.name !== 'Priya Sharma') {
          // Filter out any legacy dummy address from parsed user
          const cleanedAddrs = Array.isArray(parsed.addresses)
            ? parsed.addresses.filter((a: any) => !a.street?.includes('Flat 402, Lotus Apartments'))
            : [];
          return { ...parsed, addresses: cleanedAddrs };
        }
      }
      return null;
    } catch {
      return null;
    }
  });

  const refreshUserData = async (targetEmail: string) => {
    if (!targetEmail) return;
    const cleanEmail = targetEmail.toLowerCase().trim();
    
    // 1. Fetch from Express / Prisma backend API
    try {
      const res = await fetch(`/api/users/${encodeURIComponent(cleanEmail)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.user) {
          const u = data.user;
          setUser(prev => {
            const updated: User = {
              ...(prev || {}),
              id: u.id || prev?.id || `u-${Date.now()}`,
              name: u.name || prev?.name || 'Valued Customer',
              email: u.email || cleanEmail,
              phone: u.phone || prev?.phone || '',
              points: u.loyaltyPoints || prev?.points || 100,
              addresses: Array.isArray(u.addresses) && u.addresses.length > 0 ? u.addresses : (prev?.addresses || []),
              createdAt: u.createdAt || prev?.createdAt || new Date().toISOString()
            };
            safeStorageSet('terra_user', JSON.stringify(updated));
            return updated;
          });
          return;
        }
      }
    } catch (e) {
      console.warn('[refreshUserData API warning]:', e);
    }
  };

  // Sync profile & addresses from backend on initial mount
  useEffect(() => {
    if (user?.email) {
      refreshUserData(user.email);
    }
  }, []);

  const logoutUser = () => {
    setUser(null);
    localStorage.removeItem('terra_user');
    localStorage.removeItem('pgmart_user_token');
    sessionStorage.removeItem('pgmart_user_token');
    try {
      fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    } catch {}
  };

  const loginUser = (name: string, email: string, phone?: string, initialAddresses?: Address[]) => {
    const cleanEmail = String(email || '').toLowerCase().trim();
    // Preserve existing saved addresses if user previously saved any
    let savedAddrs: Address[] = [];
    if (initialAddresses && initialAddresses.length > 0) {
      savedAddrs = initialAddresses;
    } else {
      try {
        const saved = localStorage.getItem('terra_user');
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed?.email?.toLowerCase() === cleanEmail && Array.isArray(parsed?.addresses)) {
            savedAddrs = parsed.addresses.filter((a: Address) => !a.street?.includes('Flat 402, Lotus Apartments'));
          }
        }
      } catch {}
    }

    const newUser: User = {
      id: `u-${Date.now()}`,
      name: name || 'Valued Customer',
      email: cleanEmail || 'customer@example.com',
      phone: phone || '',
      points: 100,
      createdAt: new Date().toISOString().split('T')[0],
      addresses: savedAddrs
    };
    setUser(newUser);
    safeStorageSet('terra_user', JSON.stringify(newUser));

    // Immediately pull any saved addresses/profile from backend database
    if (cleanEmail) {
      refreshUserData(cleanEmail);
    }
  };
  const [adminToken, setAdminToken] = useState<string | null>(() => {
    return localStorage.getItem('pgmart_admin_token') || sessionStorage.getItem('pgmart_admin_token');
  });
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState<boolean>(() => {
    return Boolean(localStorage.getItem('pgmart_admin_token') || sessionStorage.getItem('pgmart_admin_token'));
  });

  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [sizeChartCategory, setSizeChartCategory] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(false);

  // Apply CSS Variables dynamically based on site settings
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', settings.primaryColor || '#C0654B');
    root.style.setProperty('--color-primary-dark', settings.primaryDarkColor || '#8B4A38');
    root.style.setProperty('--color-bg-muted', settings.bgMutedColor || '#F3E9E4');
    root.style.setProperty('--color-secondary-dark', settings.secondaryDarkColor || '#2B2620');

    // Dynamic browser favicon synchronization
    const favUrl = settings.faviconUrl || '/favicon.png';
    const iconLinks = document.querySelectorAll("link[rel*='icon']");
    if (iconLinks.length > 0) {
      iconLinks.forEach(el => {
        const linkEl = el as HTMLLinkElement;
        if (!linkEl.type || linkEl.type.includes('png') || linkEl.type.includes('icon')) {
          linkEl.href = favUrl;
        }
      });
    }
  }, [settings]);

  // Sync Cart to localStorage
  useEffect(() => {
    safeStorageSet('terra_cart', JSON.stringify(cart));
  }, [cart]);

  // Dynamic coupon discount recalculation when cart changes
  useEffect(() => {
    if (appliedCoupon) {
      const cartTotal = cart.reduce((acc, item) => acc + (item.variant.discountPrice || item.variant.price) * item.quantity, 0);
      if (cartTotal === 0) {
        setAppliedCoupon(null);
        setCouponDiscount(0);
      } else if (cartTotal < (appliedCoupon.minOrderValue || 0)) {
        setAppliedCoupon(null);
        setCouponDiscount(0);
        showToast(`Promo coupon "${appliedCoupon.code}" removed (Min. order of ₹${appliedCoupon.minOrderValue} required).`);
      } else {
        let discountAmount = 0;
        if (appliedCoupon.discountType === 'percentage') {
          discountAmount = Math.round((cartTotal * appliedCoupon.value) / 100);
          if (appliedCoupon.maxDiscount && discountAmount > appliedCoupon.maxDiscount) {
            discountAmount = appliedCoupon.maxDiscount;
          }
        } else {
          discountAmount = appliedCoupon.value;
        }
        discountAmount = Math.min(discountAmount, cartTotal);
        setCouponDiscount(discountAmount);
      }
    }
  }, [cart, appliedCoupon]);

  // Sync Wishlist to localStorage
  useEffect(() => {
    safeStorageSet('terra_wishlist', JSON.stringify(wishlist));
  }, [wishlist]);

  // Sync Banners to localStorage permanently across reloads
  useEffect(() => {
    if (isBannersLoaded) {
      safeStorageSet('pgmart_live_banners_v1', JSON.stringify(banners));
    }
  }, [banners, isBannersLoaded]);

  // Helper to safely fetch JSON from backend routes
  const safeJsonFetch = async (url: string, options?: RequestInit) => {
    try {
      const res = await fetch(url, options);
      if (res.ok) {
        const text = await res.text();
        if (text && (text.trim().startsWith('{') || text.trim().startsWith('['))) {
          return JSON.parse(text);
        }
      }
    } catch (e) {}
    return null;
  };

  // Fetch live state from Backend REST API on load
  const reloadCatalog = async () => {
    try {
      // 1. Try unified high-speed bootstrap endpoint first (1 single HTTP request)
      const bootstrapData = await safeJsonFetch('/api/bootstrap');
      if (bootstrapData && bootstrapData.success) {
        if (bootstrapData.settings) {
          setSettings(prev => {
            const merged = { ...prev, ...bootstrapData.settings };
            safeStorageSet('pgmart_site_settings', JSON.stringify(merged));
            return merged;
          });
        }
        if (bootstrapData.productDisplay) {
          setProductDisplay(prev => {
            const merged = { ...prev, ...bootstrapData.productDisplay };
            safeStorageSet('pgmart_product_display', JSON.stringify(merged));
            return merged;
          });
        }
        if (Array.isArray(bootstrapData.categories) && bootstrapData.categories.length > 0) {
          setCategories(bootstrapData.categories);
        }
        if (Array.isArray(bootstrapData.products) && bootstrapData.products.length > 0) {
          const normalizedProds = bootstrapData.products.map(normalizeProduct);
          setProducts(normalizedProds);
        }
        if (Array.isArray(bootstrapData.brands) && bootstrapData.brands.length > 0) {
          const normalizedBrands = normalizeBrands(bootstrapData.brands);
          setBrands(normalizedBrands);
          safeStorageSet('pgmart_brands', JSON.stringify(normalizedBrands));
        }
        if (Array.isArray(bootstrapData.banners)) {
          const normalizedBanners = normalizeBanners(bootstrapData.banners);
          setBanners(normalizedBanners);
          safeStorageSet('pgmart_live_banners_v1', JSON.stringify(normalizedBanners));
          setIsBannersLoaded(true);
        }
        if (Array.isArray(bootstrapData.coupons) && bootstrapData.coupons.length > 0) {
          setCoupons(bootstrapData.coupons);
          safeStorageSet('pgmart_coupons', JSON.stringify(bootstrapData.coupons));
        }
        if (Array.isArray(bootstrapData.reviews) && bootstrapData.reviews.length > 0) {
          const normalizedReviews = normalizeReviews(bootstrapData.reviews);
          setReviews(normalizedReviews);
          safeStorageSet('pgmart_reviews_v2', JSON.stringify(normalizedReviews));
        }
        return;
      }

      // Fallback to individual endpoints if bootstrap is not supported
      const [resSet, resDisplay, resCat, resProd, resOrd, resBrs, resBan, resCoup, resRev] = await Promise.all([
        safeJsonFetch('/api/settings'),
        safeJsonFetch('/api/product-display'),
        safeJsonFetch('/api/categories'),
        safeJsonFetch('/api/products'),
        safeJsonFetch('/api/orders'),
        safeJsonFetch('/api/brands'),
        safeJsonFetch('/api/banners'),
        safeJsonFetch('/api/coupons'),
        safeJsonFetch('/api/reviews')
      ]);

      if (resSet) {
        setSettings(prev => {
          const merged = { ...prev, ...resSet };
          safeStorageSet('pgmart_site_settings', JSON.stringify(merged));
          return merged;
        });
      }

      if (resDisplay) {
        const data = resDisplay.productDisplay || resDisplay;
        if (data && (Array.isArray(data.dealsOfTheDay) || data.id)) {
          setProductDisplay(prev => {
            const merged: ProductDisplay = {
              ...prev,
              ...data,
              dealsOfTheDay: Array.isArray(data.dealsOfTheDay) ? data.dealsOfTheDay : prev.dealsOfTheDay,
              dealsCount: typeof data.dealsCount === 'number' ? data.dealsCount : (Array.isArray(data.dealsOfTheDay) ? data.dealsOfTheDay.length : prev.dealsCount),
              newArrivals: Array.isArray(data.newArrivals) ? data.newArrivals : prev.newArrivals,
              newArrivalsCount: typeof data.newArrivalsCount === 'number' ? data.newArrivalsCount : (Array.isArray(data.newArrivals) ? data.newArrivals.length : prev.newArrivalsCount)
            };
            safeStorageSet('pgmart_product_display', JSON.stringify(merged));
            return merged;
          });
        }
      }

      if (resBan && Array.isArray(resBan)) {
        const normalizedBanners = normalizeBanners(resBan);
        setBanners(normalizedBanners);
        safeStorageSet('pgmart_live_banners_v1', JSON.stringify(normalizedBanners));
        setIsBannersLoaded(true);
      }

      if (resBrs && Array.isArray(resBrs) && resBrs.length > 0) {
        const normalizedBrands = normalizeBrands(resBrs);
        setBrands(normalizedBrands);
        safeStorageSet('pgmart_brands', JSON.stringify(normalizedBrands));
      }

      if (resCoup && Array.isArray(resCoup) && resCoup.length > 0) {
        setCoupons(resCoup);
        safeStorageSet('pgmart_coupons', JSON.stringify(resCoup));
      }

      if (resCat && Array.isArray(resCat) && resCat.length > 0) {
        setCategories(resCat);
      }

      if (resRev && Array.isArray(resRev) && resRev.length > 0) {
        const normalizedReviews = normalizeReviews(resRev);
        setReviews(normalizedReviews);
        safeStorageSet('pgmart_reviews_v2', JSON.stringify(normalizedReviews));
      }

      if (resProd && Array.isArray(resProd) && resProd.length > 0) {
        const normalizedProds = resProd.map(normalizeProduct);
        setProducts(normalizedProds);
      }

      if (resOrd) {
        const rawOrders = Array.isArray(resOrd) ? resOrd : (resOrd.orders || []);
        if (Array.isArray(rawOrders)) {
          const parsedOrders: Order[] = rawOrders.map((raw: any) => {
            let shippingAddress = raw.shippingAddress;
            if (typeof shippingAddress === 'string') {
              try { shippingAddress = JSON.parse(shippingAddress); } catch {}
            }
            let items = raw.items;
            if (typeof items === 'string') {
              try { items = JSON.parse(items); } catch {}
            }
            return {
              ...raw,
              shippingAddress: typeof shippingAddress === 'object' && shippingAddress !== null ? shippingAddress : {},
              items: Array.isArray(items) ? items : []
            };
          });
          setOrders(parsedOrders);
          safeStorageSet('terra_orders_v2', JSON.stringify(parsedOrders));
        }
      }
    } catch (e) {
      console.warn('Backend REST sync failed', e);
    }
  };

  useEffect(() => {
    reloadCatalog();
  }, []);

  // Periodic Refresh + Window Focus Listener (replaces WebSocket with lightweight refresh)
  useEffect(() => {
    let lastFocusReload = 0;
    const handleFocus = () => {
      const now = Date.now();
      if (now - lastFocusReload > 30000) {
        lastFocusReload = now;
        reloadCatalog();
      }
    };
    window.addEventListener('focus', handleFocus);

    // Cross-tab broadcast listener (for instantaneous sync between Admin Portal tab and Customer Storefront tab)
    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === 'pgmart_site_settings' && e.newValue) {
        try { setSettings(JSON.parse(e.newValue)); } catch {}
      }
      if (e.key === 'pgmart_product_display' && e.newValue) {
        try { setProductDisplay(JSON.parse(e.newValue)); } catch {}
      }
      if (e.key === 'pgmart_live_banners_v1' && e.newValue) {
        try { setBanners(JSON.parse(e.newValue)); } catch {}
      }
      if (e.key === 'pgmart_brands' && e.newValue) {
        try { setBrands(normalizeBrands(JSON.parse(e.newValue))); } catch {}
      }
      if (e.key === 'pgmart_coupons' && e.newValue) {
        try { setCoupons(JSON.parse(e.newValue)); } catch {}
      }
      if (e.key === 'pgmart_reviews_v2' && e.newValue) {
        try { setReviews(normalizeReviews(JSON.parse(e.newValue))); } catch {}
      }
      if (e.key === 'pgmart_categories_v2' && e.newValue) {
        try { setCategories(JSON.parse(e.newValue)); } catch {}
      }
      if (e.key === 'terra_products_v8' && e.newValue) {
        try { setProducts(JSON.parse(e.newValue)); } catch {}
      }
      if (e.key === 'terra_orders_v2' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (Array.isArray(parsed)) {
            setOrders(parsed);
            window.dispatchEvent(new CustomEvent('pgmart_order_updated', { detail: { sync: true } }));
          }
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorageEvent);

    const handleCatalogUpdate = () => {
      reloadCatalog();
    };
    window.addEventListener('pgmart_catalog_updated', handleCatalogUpdate);

    const catalogInterval = setInterval(() => {
      if (!document.hidden) {
        reloadCatalog();
      }
    }, 60000);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorageEvent);
      window.removeEventListener('pgmart_catalog_updated', handleCatalogUpdate);
      clearInterval(catalogInterval);
    };
  }, []);

  // Automatic localStorage persistence for orders
  useEffect(() => {
    safeStorageSet('terra_orders_v2', JSON.stringify(orders));
  }, [orders]);

  // Real-time Order Sync (Polls /api/orders when tab is active with connection guard)
  useEffect(() => {
    let isFetchingOrders = false;
    const syncOrders = async () => {
      if (document.hidden || isFetchingOrders) return;
      isFetchingOrders = true;
      try {
        const res = await fetch('/api/orders');
        if (res.ok) {
          const dbData = await res.json();
          const dbOrders: Order[] = Array.isArray(dbData) ? dbData : (dbData?.orders || []);
          if (Array.isArray(dbOrders)) {
            setOrders(prev => {
              // Authoritative database orders directly from server
              const dbIds = new Set(dbOrders.map((o: Order) => o.id));
              // Only preserve freshly placed local orders created in the last 20 seconds that may not have reached server yet
              const twentySecondsAgo = Date.now() - 20000;
              const localPending = prev.filter(o => !dbIds.has(o.id) && new Date(o.createdAt || 0).getTime() > twentySecondsAgo);
              const nextOrders = [...dbOrders, ...localPending];

              // Check if identical to avoid useless state reference updates
              if (prev.length === nextOrders.length) {
                const isIdentical = prev.every((p, idx) => {
                  const n = nextOrders[idx];
                  return (
                    n &&
                    p.id === n.id &&
                    p.status === n.status &&
                    p.trackingNumber === n.trackingNumber &&
                    p.courierPartner === n.courierPartner &&
                    p.invoiceNumber === n.invoiceNumber &&
                    p.paymentStatus === n.paymentStatus &&
                    p.returnStatus === n.returnStatus &&
                    p.updatedAt === n.updatedAt
                  );
                });
                if (isIdentical) return prev;
              }
              return nextOrders;
            });
          }
        }
      } catch (e) {
      } finally {
        isFetchingOrders = false;
      }
    };

    const syncOrdersInterval = setInterval(syncOrders, 20000);
    return () => clearInterval(syncOrdersInterval);
  }, []);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const updateProductDisplay = async (newDisplay: Partial<ProductDisplay>) => {
    let computedCountDisplay: Partial<ProductDisplay> = { ...newDisplay };
    if (newDisplay.dealsOfTheDay !== undefined && newDisplay.dealsCount === undefined) {
      computedCountDisplay.dealsCount = Array.isArray(newDisplay.dealsOfTheDay) ? newDisplay.dealsOfTheDay.length : 0;
    }
    if (newDisplay.newArrivals !== undefined && newDisplay.newArrivalsCount === undefined) {
      computedCountDisplay.newArrivalsCount = Array.isArray(newDisplay.newArrivals) ? newDisplay.newArrivals.length : 0;
    }

    setProductDisplay(prev => {
      const deals = computedCountDisplay.dealsOfTheDay !== undefined ? computedCountDisplay.dealsOfTheDay : prev.dealsOfTheDay;
      const na = computedCountDisplay.newArrivals !== undefined ? computedCountDisplay.newArrivals : prev.newArrivals;
      const dCount = computedCountDisplay.dealsCount !== undefined ? computedCountDisplay.dealsCount : (Array.isArray(deals) ? deals.length : (prev.dealsCount ?? 0));
      const naCount = computedCountDisplay.newArrivalsCount !== undefined ? computedCountDisplay.newArrivalsCount : (Array.isArray(na) ? na.length : (prev.newArrivalsCount ?? 0));

      const merged: ProductDisplay = {
        ...prev,
        ...computedCountDisplay,
        dealsOfTheDay: deals,
        dealsCount: dCount,
        newArrivals: na,
        newArrivalsCount: naCount
      };
      safeStorageSet('pgmart_product_display', JSON.stringify(merged));
      return merged;
    });

    const candidateEndpoints = [
      '/api/product-display.php',
      '/api/product-display',
      '/api/admin/product-display',
      '/api/product_display'
    ];

    for (const ep of candidateEndpoints) {
      try {
        const res = await adminFetch(ep, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(computedCountDisplay)
        });
        if (res && res.ok) {
          const text = await res.text();
          if (text && (text.startsWith('{') || text.startsWith('['))) {
            const data = JSON.parse(text);
            const returned = data?.productDisplay || data;
            if (returned && (returned.dealsOfTheDay || returned.dealsTitle || returned.id)) {
              setProductDisplay(prev => {
                const updated: ProductDisplay = {
                  ...prev,
                  ...returned,
                  dealsOfTheDay: Array.isArray(returned.dealsOfTheDay) ? returned.dealsOfTheDay : prev.dealsOfTheDay,
                  dealsCount: typeof returned.dealsCount === 'number' ? returned.dealsCount : (Array.isArray(returned.dealsOfTheDay) ? returned.dealsOfTheDay.length : prev.dealsCount),
                  newArrivals: Array.isArray(returned.newArrivals) ? returned.newArrivals : prev.newArrivals,
                  newArrivalsCount: typeof returned.newArrivalsCount === 'number' ? returned.newArrivalsCount : (Array.isArray(returned.newArrivals) ? returned.newArrivals.length : prev.newArrivalsCount)
                };
                safeStorageSet('pgmart_product_display', JSON.stringify(updated));
                return updated;
              });
              break;
            }
          }
        }
      } catch (err) {
        // Fallback to next endpoint
      }
    }
  };

  const updateSettings = async (newSettings: Partial<SiteSettings>) => {
    setSettings(prev => {
      const merged = { ...prev, ...newSettings };
      safeStorageSet('pgmart_site_settings', JSON.stringify(merged));
      return merged;
    });

    // Also keep ProductDisplay table in sync for Deals and New Arrivals fields
    const displayUpdates: Partial<ProductDisplay> = {};
    if (newSettings.dealsEnabled !== undefined) displayUpdates.dealsEnabled = newSettings.dealsEnabled;
    if (newSettings.dealsTitle !== undefined) displayUpdates.dealsTitle = newSettings.dealsTitle;
    if (newSettings.dealsTimerHours !== undefined) displayUpdates.dealsTimerHours = newSettings.dealsTimerHours;
    if (newSettings.dealsTimerMinutes !== undefined) displayUpdates.dealsTimerMinutes = newSettings.dealsTimerMinutes;
    if (newSettings.dealsMinDiscount !== undefined) displayUpdates.dealsMinDiscount = newSettings.dealsMinDiscount;
    if (newSettings.newArrivalsEnabled !== undefined) displayUpdates.newArrivalsEnabled = newSettings.newArrivalsEnabled;
    if (newSettings.newArrivalsTitle !== undefined) displayUpdates.newArrivalsTitle = newSettings.newArrivalsTitle;
    if (newSettings.newArrivalsSubtitle !== undefined) displayUpdates.newArrivalsSubtitle = newSettings.newArrivalsSubtitle;
    if (newSettings.newArrivalsBadge !== undefined) displayUpdates.newArrivalsBadge = newSettings.newArrivalsBadge;
    if (newSettings.newArrivalsMaxItems !== undefined) displayUpdates.newArrivalsMaxItems = newSettings.newArrivalsMaxItems;

    if (Object.keys(displayUpdates).length > 0) {
      updateProductDisplay(displayUpdates);
    }

    try {
      await adminFetch('/api/admin/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings)
      });
    } catch (apiErr) {
      try {
        await fetch('/api/settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newSettings)
        });
      } catch (e) {}
    }
  };

  const addToCart = (product: Product, variant: ProductVariant, qty = 1) => {
    // 1. Strict stock & availability verification
    const variantStock = typeof variant?.stock === 'number' ? variant.stock : undefined;
    const productStock = typeof product?.stock === 'number' ? product.stock : undefined;

    if (product?.status === 'out_of_stock' || (variantStock !== undefined && variantStock <= 0) || (productStock !== undefined && productStock <= 0)) {
      showToast(`Sorry, ${product?.name || 'Product'} (${variant?.size || ''} ${variant?.color || ''}) is currently unavailable.`);
      return;
    }

    const maxStock = variantStock !== undefined ? variantStock : (productStock !== undefined ? productStock : 99);

    const existingIndex = cart.findIndex(c => c.variant.id === variant.id);
    if (existingIndex > -1) {
      const currentQty = cart[existingIndex].quantity;
      if (currentQty + qty > maxStock) {
        showToast(`Only ${maxStock} item(s) available in stock.`);
        return;
      }
      const newCart = [...cart];
      newCart[existingIndex].quantity += qty;
      setCart(newCart);
    } else {
      if (qty > maxStock) {
        showToast(`Only ${maxStock} item(s) available in stock.`);
        return;
      }
      setCart(prev => [
        ...prev,
        {
          id: `cart-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
          product,
          variant,
          quantity: qty,
          selectedColor: variant.color,
          selectedSize: variant.size
        }
      ]);
    }
    showToast(`Added ${product.name} (${variant.size}) to bag!`);
  };

  const removeFromCart = (cartItemId: string) => {
    setCart(prev => prev.filter(item => item.id !== cartItemId));
  };

  const updateCartQty = (cartItemId: string, qty: number) => {
    if (qty <= 0) {
      removeFromCart(cartItemId);
      return;
    }
    const item = cart.find(c => c.id === cartItemId);
    if (item) {
      const maxStock = typeof item.variant?.stock === 'number' ? item.variant.stock : (typeof item.product?.stock === 'number' ? item.product.stock : 99);
      if (qty > maxStock) {
        showToast(`Only ${maxStock} item(s) available in stock.`);
        return;
      }
    }
    setCart(prev => prev.map(item => item.id === cartItemId ? { ...item, quantity: qty } : item));
  };

  const clearCart = () => {
    setCart([]);
    setAppliedCoupon(null);
    setCouponDiscount(0);
  };

  const saveCoupon = async (coupon: Coupon) => {
    setCoupons(prev => {
      const updated = prev.some(c => c.id === coupon.id || c.code.toUpperCase() === coupon.code.toUpperCase())
        ? prev.map(c => (c.id === coupon.id || c.code.toUpperCase() === coupon.code.toUpperCase()) ? coupon : c)
        : [coupon, ...prev];
      safeStorageSet('pgmart_coupons', JSON.stringify(updated));
      return updated;
    });

    try {
      await adminFetch('/api/coupons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(coupon)
      });
    } catch (e) {
      console.warn('[saveCoupon REST error]:', e);
    }
  };

  const toggleCoupon = async (id: string) => {
    const target = coupons.find(c => c.id === id);
    const newStatus = target ? !target.isActive : true;
    setCoupons(prev => {
      const updated = prev.map(c => c.id === id ? { ...c, isActive: newStatus } : c);
      safeStorageSet('pgmart_coupons', JSON.stringify(updated));
      return updated;
    });

    try {
      await adminFetch(`/api/coupons/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: newStatus })
      });
    } catch (e) {
      console.warn('[toggleCoupon REST error]:', e);
    }
  };

  const deleteCoupon = async (id: string) => {
    setCoupons(prev => {
      const updated = prev.filter(c => c.id !== id);
      safeStorageSet('pgmart_coupons', JSON.stringify(updated));
      return updated;
    });

    try {
      await adminFetch(`/api/coupons/${id}`, {
        method: 'DELETE'
      });
    } catch (e) {
      console.warn('[deleteCoupon REST error]:', e);
    }
  };

  const saveBrand = async (brand: Brand) => {
    setBrands(prev => {
      const updated = prev.some(b => b.id === brand.id)
        ? prev.map(b => b.id === brand.id ? brand : b)
        : [brand, ...prev];
      safeStorageSet('pgmart_brands', JSON.stringify(updated));
      return updated;
    });

    try {
      await adminFetch('/api/brands', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(brand)
      });
    } catch (e) {}
  };

  const toggleBrand = async (id: string) => {
    const target = brands.find(b => b.id === id);
    const newStatus = target ? !target.isActive : true;
    setBrands(prev => {
      const updated = prev.map(c => c.id === id ? { ...c, isActive: newStatus } : c);
      safeStorageSet('pgmart_brands', JSON.stringify(updated));
      return updated;
    });

    try {
      await adminFetch(`/api/brands/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: newStatus })
      });
    } catch (e) {}
  };

  const deleteBrand = async (id: string) => {
    setBrands(prev => {
      const updated = prev.filter(b => b.id !== id);
      safeStorageSet('pgmart_brands', JSON.stringify(updated));
      return updated;
    });

    try {
      await adminFetch(`/api/brands/${id}`, {
        method: 'DELETE'
      });
    } catch (e) {}
  };

  const saveBanner = async (banner: Banner): Promise<Banner> => {
    let savedBannerObj = normalizeBanner(banner);

    setBanners(prev => {
      const isAd = savedBannerObj.position === 'ad_banner' || savedBannerObj.position === 'promo_strip';
      let updated: Banner[];
      if (prev.some(b => b.id === savedBannerObj.id)) {
        // In-place update preserving exact card position in list
        updated = prev.map(b => b.id === savedBannerObj.id ? savedBannerObj : b);
      } else if (isAd) {
        // If adding new ad banner, replace any existing ad banner in place or append
        const hasAd = prev.some(b => b.position === 'ad_banner' || b.position === 'promo_strip');
        if (hasAd) {
          updated = prev.map(b => (b.position === 'ad_banner' || b.position === 'promo_strip') ? savedBannerObj : b);
        } else {
          updated = [savedBannerObj, ...prev];
        }
      } else {
        updated = [savedBannerObj, ...prev];
      }
      safeStorageSet('pgmart_live_banners_v1', JSON.stringify(updated));
      return updated;
    });

    try {
      const res = await adminFetch('/api/banners', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(savedBannerObj)
      });
      if (res && res.ok) {
        const text = await res.text();
        if (text && (text.startsWith('{') || text.startsWith('['))) {
          const data = JSON.parse(text);
          if (data && data.success && data.banner) {
            savedBannerObj = normalizeBanner(data.banner);
            setBanners(prev => {
              const isAd = savedBannerObj.position === 'ad_banner' || savedBannerObj.position === 'promo_strip';
              let updated: Banner[];
              if (prev.some(b => b.id === savedBannerObj.id)) {
                updated = prev.map(b => b.id === savedBannerObj.id ? savedBannerObj : b);
              } else if (isAd) {
                const hasAd = prev.some(b => b.position === 'ad_banner' || b.position === 'promo_strip');
                if (hasAd) {
                  updated = prev.map(b => (b.position === 'ad_banner' || b.position === 'promo_strip') ? savedBannerObj : b);
                } else {
                  updated = [savedBannerObj, ...prev];
                }
              } else {
                updated = [savedBannerObj, ...prev];
              }
              safeStorageSet('pgmart_live_banners_v1', JSON.stringify(updated));
              return updated;
            });
          }
        }
      }
    } catch (e) {
      console.warn('[saveBanner REST error]:', e);
    }
    return savedBannerObj;
  };

  const toggleBanner = async (id: string) => {
    let newStatus = true;
    setBanners(prev => {
      const target = prev.find(b => b.id === id);
      newStatus = target ? !target.isActive : false;
      const updated = prev.map(b => b.id === id ? { ...b, isActive: newStatus } : b);
      safeStorageSet('pgmart_live_banners_v1', JSON.stringify(updated));
      return updated;
    });

    try {
      await adminFetch(`/api/banners/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: newStatus })
      });
    } catch (e) {
      console.warn('[toggleBanner REST error]:', e);
    }
  };

  const deleteBanner = async (id: string) => {
    setBanners(prev => {
      const updated = prev.filter(b => b.id !== id);
      safeStorageSet('pgmart_live_banners_v1', JSON.stringify(updated));
      return updated;
    });

    try {
      await adminFetch(`/api/banners/${id}`, {
        method: 'DELETE'
      });
    } catch (e) {
      console.warn('[deleteBanner REST error]:', e);
    }
  };

  const applyCoupon = async (code: string) => {
    const cartTotal = cart.reduce((acc, item) => acc + (item.variant.discountPrice || item.variant.price) * item.quantity, 0);
    const cleanCode = code.trim().toUpperCase();

    if (!cleanCode) {
      showToast('Please enter a coupon code.');
      return { success: false, message: 'Please enter a coupon code.' };
    }

    try {
      const res = await fetch('/api/coupons/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: cleanCode, cartTotal })
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        setAppliedCoupon(data.coupon);
        setCouponDiscount(data.discountAmount);
        showToast(`🎉 Coupon ${cleanCode} applied! Saved ₹${data.discountAmount}`);
        return { success: true, message: `Coupon applied: ₹${data.discountAmount} off` };
      } else if (data && (data.error || data.message)) {
        const errMsg = data.error || data.message;
        setAppliedCoupon(null);
        setCouponDiscount(0);
        showToast(`❌ ${errMsg}`);
        return { success: false, message: errMsg };
      }
    } catch (e) {
      console.warn('Backend coupon validate offline fallback', e);
    }

    // Local state fallback validation (if offline)
    const coupon = coupons.find(c => c.code.toUpperCase() === cleanCode);
    if (!coupon) {
      showToast(`❌ Invalid promo code "${cleanCode}".`);
      return { success: false, message: `Invalid promo code "${cleanCode}".` };
    }
    if (!coupon.isActive) {
      showToast(`❌ Promo coupon code "${cleanCode}" is currently paused.`);
      return { success: false, message: `Promo coupon code "${cleanCode}" is currently paused.` };
    }
    if (coupon.expiryDate && new Date(coupon.expiryDate) < new Date(new Date().setHours(0, 0, 0, 0))) {
      showToast(`❌ Promo coupon code "${cleanCode}" has expired.`);
      return { success: false, message: `Promo coupon code "${cleanCode}" has expired.` };
    }
    if (coupon.usageLimit > 0 && (coupon.usedCount || 0) >= coupon.usageLimit) {
      showToast(`❌ Promo coupon code "${cleanCode}" usage limit has been reached.`);
      return { success: false, message: `Promo coupon code "${cleanCode}" usage limit has been reached.` };
    }
    if (cartTotal < (coupon.minOrderValue || 0)) {
      const msg = `Minimum order value of ₹${(coupon.minOrderValue || 0).toLocaleString('en-IN')} required for ${cleanCode}.`;
      showToast(`❌ ${msg}`);
      return { success: false, message: msg };
    }

    let discountAmount = 0;
    if (coupon.discountType === 'percentage') {
      discountAmount = Math.round(cartTotal * (coupon.value / 100));
      if (coupon.maxDiscount && discountAmount > coupon.maxDiscount) {
        discountAmount = coupon.maxDiscount;
      }
    } else {
      discountAmount = coupon.value;
    }

    discountAmount = Math.min(discountAmount, cartTotal);
    setAppliedCoupon(coupon);
    setCouponDiscount(discountAmount);
    showToast(`🎉 Coupon ${cleanCode} applied! Saved ₹${discountAmount}`);
    return { success: true, message: `Coupon applied: ₹${discountAmount} off` };
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    showToast('Coupon removed');
  };

  const toggleWishlist = (productId: string) => {
    setWishlist(prev => {
      if (prev.includes(productId)) {
        showToast('Removed from Wishlist');
        return prev.filter(id => id !== productId);
      } else {
        showToast('Saved to Wishlist');
        return [...prev, productId];
      }
    });
  };

  const adminLogin = async (password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const cleanPass = (password || '').trim();
      if (!cleanPass) {
        return { success: false, error: 'Please enter administrator password.' };
      }

      const endpoints = ['/api/admin/login', '/api/admin-auth.php', '/api/admin-auth'];
      let lastErrMsg = '';

      for (const endpoint of endpoints) {
        try {
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ password: cleanPass }),
          });

          const contentType = res.headers.get('content-type') || '';
          if (contentType.includes('application/json') || res.status === 401 || res.status === 400 || res.ok) {
            const text = await res.text();
            if (text && !text.trim().startsWith('<')) {
              const data = JSON.parse(text);
              if (res.ok && data?.token) {
                setAdminToken(data.token);
                setIsAdminLoggedIn(true);
                localStorage.setItem('pgmart_admin_token', data.token);
                sessionStorage.setItem('pgmart_admin_token', data.token);
                showToast('Logged in as Store Administrator');
                return { success: true };
              }
              if (data?.error) {
                lastErrMsg = data.error;
              }
            }
          }
        } catch (err) {
          // Fall through to next candidate endpoint
        }
      }

      // 3. Secure direct client-side fallback if hosted on static hosting
      const EXPECTED_TOKEN = 'pgmartsanjeevandnavin@2026';
      if (cleanPass === EXPECTED_TOKEN) {
        setAdminToken(cleanPass);
        setIsAdminLoggedIn(true);
        localStorage.setItem('pgmart_admin_token', cleanPass);
        sessionStorage.setItem('pgmart_admin_token', cleanPass);
        showToast('Logged in as Store Administrator');
        return { success: true };
      }

      return { success: false, error: lastErrMsg || 'Invalid administrator password.' };
    } catch (e: any) {
      console.warn('Admin login request failed:', e);
      return { success: false, error: 'Cannot connect to server. Please verify your connection.' };
    }
  };

  const adminLogout = () => {
    setAdminToken(null);
    setIsAdminLoggedIn(false);
    localStorage.removeItem('pgmart_admin_token');
    sessionStorage.removeItem('pgmart_admin_token');
    showToast('Admin logged out');
  };

  const saveSettings = async (newSettings: Partial<SiteSettings>) => {
    await updateSettings(newSettings);
  };

  const createProduct = async (productData: Partial<Product>) => {
    const stockVal = typeof productData.stock === 'number' ? Math.max(0, productData.stock) : 50;
    const initialStatus = stockVal === 0 ? 'out_of_stock' : (productData.status || 'published');

    const newProduct: Product = {
      id: productData.id || `p-${Date.now()}`,
      name: productData.name || 'New Stylish Apparel',
      slug: productData.slug || (productData.name ? productData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') : `item-${Date.now()}`),
      categoryId: productData.categoryId || 'women',
      subcategoryId: productData.subcategoryId || 'women-ethnic',
      typeId: productData.typeId || 'saree',
      brandId: productData.brandId || 'b1',
      brandName: productData.brandName || 'PGmart Essentials',
      description: productData.description || 'Premium quality fabric crafted with high precision and luxury finish.',
      fabric: productData.fabric || 'Cotton Silk Blend',
      fit: productData.fit || 'Regular Fit',
      occasion: productData.occasion || 'Festive / Casual',
      basePrice: Number(productData.basePrice) || 1999,
      discountPrice: productData.discountPrice ? Number(productData.discountPrice) : undefined,
      discountPercent: productData.discountPercent ? Number(productData.discountPercent) : undefined,
      images: Array.isArray(productData.images) ? productData.images : [],
      tags: productData.tags || ['new_arrival'],
      isDealOfTheDay: Boolean(productData.isDealOfTheDay),
      isNonReturnable: Boolean(productData.isNonReturnable || productData.noReturn),
      noReturn: Boolean(productData.isNonReturnable || productData.noReturn),
      status: initialStatus,
      stock: stockVal,
      availableSizes: productData.availableSizes || ['S', 'M', 'L', 'XL'],
      colors: productData.colors || [{ name: 'Default', hex: '#C0654B', images: [] }],
      rating: productData.rating || 5,
      reviewCount: productData.reviewCount || 0,
      variants: productData.variants || [],
      hsnCode: productData.hsnCode || '5407',
      gstPercent: productData.gstPercent || (((productData.discountPrice || productData.basePrice || 0) > 2500) ? 18 : 5),
      created_at: productData.created_at || new Date().toISOString()
    };

    setProducts(prev => {
      if (prev.some(p => p.id === newProduct.id)) {
        return prev.map(p => p.id === newProduct.id ? newProduct : p);
      }
      return [newProduct, ...prev];
    });

    try {
      const res = await adminFetch('/api/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newProduct)
      });
      const contentType = res?.headers?.get('content-type') || '';
      if (res && res.ok && contentType.includes('application/json')) {
        const data = await res.json();
        if (data.success && data.product) {
          const savedProd = data.product;
          setProducts(prev => {
            if (prev.some(p => p.id === savedProd.id)) {
              return prev.map(p => p.id === savedProd.id ? savedProd : p);
            }
            return [savedProd, ...prev];
          });
        }
      }
    } catch (e) {
      console.warn('[REST API createProduct Error]:', e);
    }
    showToast('Product created successfully');
  };

  const updateProduct = async (id: string, productData: Partial<Product>) => {
    const isNonReturnableVal = productData.isNonReturnable !== undefined 
      ? Boolean(productData.isNonReturnable) 
      : (productData.noReturn !== undefined ? Boolean(productData.noReturn) : undefined);

    const mergedData = {
      ...productData,
      ...(isNonReturnableVal !== undefined ? { isNonReturnable: isNonReturnableVal, noReturn: isNonReturnableVal } : {})
    };

    setProducts(prev => prev.map(p => p.id === id ? { ...p, ...mergedData } : p));

    try {
      const res = await adminFetch(`/api/products/${id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(productData)
      });
      const contentType = res?.headers?.get('content-type') || '';
      if (res && res.ok && contentType.includes('application/json')) {
        const data = await res.json();
        if (data.success && data.product) {
          const updatedProd = data.product;
          setProducts(prev => prev.map(p => p.id === id ? { ...p, ...updatedProd } : p));
        }
      }
    } catch (e) {
      console.warn('[REST API updateProduct Error]:', e);
    }
  };

  const updateProductStock = async (productId: string, newStock: number) => {
    const validStock = Math.max(0, newStock);
    const newStatus = validStock === 0 ? 'out_of_stock' : undefined;
    setProducts(prev => prev.map(p => {
      if (p.id !== productId) return p;
      return {
        ...p,
        stock: validStock,
        ...(newStatus ? { status: newStatus } : (p.status === 'out_of_stock' && validStock > 0 ? { status: 'published' } : {}))
      };
    }));

    try {
      const res = await adminFetch(`/api/products/${productId}/stock`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stock: validStock })
      });
      if (res && res.ok) {
        const data = await res.json();
        if (data.success && data.product) {
          setProducts(prev => prev.map(p => p.id === productId ? { ...p, ...data.product } : p));
        }
      }
    } catch (e) {
      console.warn('[REST updateProductStock error]:', e);
    }
  };

  const deleteProduct = async (id: string) => {
    setProducts(prev => prev.filter(p => p.id !== id));

    try {
      const res = await adminFetch(`/api/products/${id}`, {
        method: 'DELETE'
      });
      if (res && res.ok) {
        showToast('Product deleted from database');
        return;
      }
    } catch (e) {
      console.warn('Backend sync failed for product deletion', e);
    }
    showToast('Product deleted');
  };

  const createOrder = async (orderData: Partial<Order>): Promise<Order | null> => {
    try {
      let createdOrder: Order | null = null;

      const endpoints = ['/api/orders', '/api/orders.php'];
      for (const ep of endpoints) {
        try {
          const res = await fetch(ep, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(orderData)
          });
          const cType = res.headers.get('content-type') || '';
          if (res.ok && cType.includes('application/json')) {
            const data = await res.json();
            if (data && data.success && data.order) {
              createdOrder = data.order;
              break;
            }
          }
        } catch (apiErr) {
          console.warn(`[createOrder ${ep} notice]:`, apiErr);
        }
      }

      if (createdOrder) {
        setOrders(prev => {
          const next = [createdOrder!, ...prev.filter(o => o.id !== createdOrder!.id)];
          safeStorageSet('terra_orders_v2', JSON.stringify(next));
          return next;
        });

        // Update local user stats if logged in
        if (user) {
          setUser(prevUser => {
            if (!prevUser) return null;
            const updatedUser = {
              ...prevUser,
              ordersCount: (prevUser.ordersCount || 0) + 1,
              totalSpent: (prevUser.totalSpent || 0) + (createdOrder!.total || 0)
            };
            safeStorageSet('pgmart_user', JSON.stringify(updatedUser));
            return updatedUser;
          });
        }

        window.dispatchEvent(new CustomEvent('pgmart_order_updated', { detail: { order: createdOrder } }));
        clearCart();
        showToast(`🎉 Order #${createdOrder.orderNumber} successfully placed!`);
        return createdOrder;
      }

      // Local fallback Order Creation if offline
      const fallbackOrder: Order = {
        id: orderData.id || `ord-${Date.now()}`,
        orderNumber: orderData.orderNumber || `PGM-${Math.floor(100000 + Math.random() * 900000)}`,
        customerId: orderData.customerId || user?.id || 'guest',
        customerName: orderData.customerName || user?.name || 'Valued Customer',
        customerEmail: orderData.customerEmail || user?.email || '',
        customerPhone: orderData.customerPhone || '',
        shippingAddress: orderData.shippingAddress || {} as any,
        items: orderData.items || [],
        subtotal: orderData.subtotal || 0,
        discount: orderData.discount || 0,
        shippingFee: orderData.shippingFee || 0,
        tax: orderData.tax || 0,
        total: orderData.total || 0,
        status: 'pending',
        paymentStatus: orderData.paymentStatus || 'pending',
        paymentMethod: orderData.paymentMethod || 'cod',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isStockDeducted: false
      };

      setOrders(prev => {
        const next = [fallbackOrder, ...prev];
        safeStorageSet('terra_orders_v2', JSON.stringify(next));
        return next;
      });

      if (user) {
        setUser(prevUser => {
          if (!prevUser) return null;
          const updatedUser = {
            ...prevUser,
            ordersCount: (prevUser.ordersCount || 0) + 1,
            totalSpent: (prevUser.totalSpent || 0) + (fallbackOrder.total || 0)
          };
          safeStorageSet('pgmart_user', JSON.stringify(updatedUser));
          return updatedUser;
        });
      }

      window.dispatchEvent(new CustomEvent('pgmart_order_updated', { detail: { order: fallbackOrder } }));
      clearCart();
      showToast('Your order has been submitted!');
      return fallbackOrder;

    } catch (err: any) {
      console.error('[createOrder Exception]:', err);
      showToast(err?.message || 'Network error while placing order. Please check your connection.');
      return null;
    }
  };

  const setOrderInvoiceNumber = async (orderId: string, invoiceNumber: string) => {
    const cleanInvoiceNo = invoiceNumber.trim();
    if (!cleanInvoiceNo) return;
    const nowIso = new Date().toISOString();

    setOrders(prev => {
      const next = prev.map(o => o.id === orderId ? { ...o, invoiceNumber: cleanInvoiceNo, invoiceDate: nowIso, updatedAt: nowIso } : o);
      safeStorageSet('terra_orders_v2', JSON.stringify(next));
      return next;
    });

    window.dispatchEvent(new CustomEvent('pgmart_order_updated', {
      detail: { orderId, invoiceNumber: cleanInvoiceNo }
    }));

    showToast(`✅ Tax Invoice No. "${cleanInvoiceNo}" assigned to Order #${orderId}`);

    try {
      await adminFetch(`/api/orders/${orderId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceNumber: cleanInvoiceNo, invoiceDate: nowIso })
      });
    } catch (e) {}
  };

  const updateOrderStatus = async (orderId: string, status: Order['status'], trackingNum?: string, orderObj?: Order, invoiceNum?: string) => {
    const isDelivered = status === 'delivered';
    const isConfirming = status === 'confirmed';
    const targetOrder = orderObj || orders.find(o => o.id === orderId);
    const nowIso = new Date().toISOString();

    let stockDeductedNow = false;
    let stockRestoredNow = false;

    // 1. DEDUCT QUANTITY IF TRANSITIONING TO CONFIRMED FOR FIRST TIME
    if (isConfirming && targetOrder && !targetOrder.isStockDeducted) {
      stockDeductedNow = true;
      const orderItems = Array.isArray(targetOrder.items) ? targetOrder.items : [];
      
      setProducts(prevProducts => {
        const updated = [...prevProducts];
        orderItems.forEach(item => {
          if (!item) return;
          const pId = item.productId || (item as any).product?.id || item.id;
          const pIndex = updated.findIndex(p => p.id === pId || p.name === item.name || (item as any).productName === p.name);
          if (pIndex !== -1) {
            const prod = updated[pIndex];
            const currentStock = typeof prod.stock === 'number' ? prod.stock : 50;
            const itemQty = Number(item.quantity || 1);
            const newStock = Math.max(0, currentStock - itemQty);
            const newStatus = newStock === 0 ? 'out_of_stock' : prod.status;

            updated[pIndex] = {
              ...prod,
              stock: newStock,
              status: newStatus
            };

            adminFetch(`/api/products/${prod.id}/stock`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ stock: newStock })
            }).catch(() => {});
          }
        });
        return updated;
      });
    }

    // 2. RESTORE QUANTITY IF CONFIRMED ORDER IS LATER CANCELLED OR RETURNED
    if ((status === 'cancelled' || status === 'returned') && targetOrder && targetOrder.isStockDeducted) {
      stockRestoredNow = true;
      const orderItems = Array.isArray(targetOrder.items) ? targetOrder.items : [];

      setProducts(prevProducts => {
        const updated = [...prevProducts];
        orderItems.forEach(item => {
          if (!item) return;
          const pId = item.productId || (item as any).product?.id || item.id;
          const pIndex = updated.findIndex(p => p.id === pId || p.name === item.name || (item as any).productName === p.name);
          if (pIndex !== -1) {
            const prod = updated[pIndex];
            const currentStock = typeof prod.stock === 'number' ? prod.stock : 0;
            const itemQty = Number(item.quantity || 1);
            const newStock = currentStock + itemQty;
            const newStatus = prod.status === 'out_of_stock' && newStock > 0 ? 'published' : prod.status;

            updated[pIndex] = {
              ...prod,
              stock: newStock,
              status: newStatus
            };

            adminFetch(`/api/products/${prod.id}/stock`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ stock: newStock })
            }).catch(() => {});
          }
        });
        return updated;
      });
    }

    const isCancelled = status === 'cancelled';
    const effectiveCancelledBy = (orderObj as any)?.cancelledBy || (isCancelled ? (targetOrder?.cancelledBy || 'admin') : undefined);
    const effectiveCancellationReason = (orderObj as any)?.cancellationReason || (isCancelled ? targetOrder?.cancellationReason : undefined);
    const effectiveCancelledAt = isCancelled ? ((orderObj as any)?.cancelledAt || targetOrder?.cancelledAt || nowIso) : undefined;

    // 2. Immediate optimistic update in Local React State & LocalStorage
    setOrders(prev => {
      const next = prev.map(o => {
        if (o.id === orderId) {
          const courier = (orderObj as any)?.courierPartner || o.courierPartner || 'Delhivery';
          const waybill = (orderObj as any)?.delhiveryWaybill || (courier === 'Delhivery' && trackingNum) || o.delhiveryWaybill;
          return {
            ...o,
            status,
            trackingNumber: trackingNum || o.trackingNumber,
            courierPartner: courier,
            delhiveryWaybill: waybill,
            invoiceNumber: invoiceNum || o.invoiceNumber,
            invoiceDate: invoiceNum ? (o.invoiceDate || nowIso) : o.invoiceDate,
            paymentStatus: isDelivered ? 'paid' : o.paymentStatus,
            isStockDeducted: stockDeductedNow ? true : (stockRestoredNow ? false : o.isStockDeducted),
            cancelledBy: isCancelled ? (effectiveCancelledBy || o.cancelledBy) : o.cancelledBy,
            cancelledAt: isCancelled ? (effectiveCancelledAt || o.cancelledAt || nowIso) : o.cancelledAt,
            cancellationReason: isCancelled ? (effectiveCancellationReason || o.cancellationReason) : o.cancellationReason,
            updatedAt: nowIso
          };
        }
        return o;
      });
      safeStorageSet('terra_orders_v2', JSON.stringify(next));
      return next;
    });

    // 3. Dispatch global live event
    window.dispatchEvent(new CustomEvent('pgmart_order_updated', {
      detail: { 
        orderId, 
        status, 
        trackingNumber: trackingNum, 
        delhiveryWaybill: (orderObj as any)?.delhiveryWaybill || trackingNum,
        courierPartner: (orderObj as any)?.courierPartner || 'Delhivery',
        invoiceNumber: invoiceNum,
        cancelledBy: effectiveCancelledBy,
        cancelledAt: effectiveCancelledAt,
        cancellationReason: effectiveCancellationReason
      }
    }));

    const toastMsg = stockDeductedNow
      ? `Order #${orderId} status updated to "${status.toUpperCase()}" & Quantity Deducted from Product List!`
      : `Order #${orderId} status updated to "${status.toUpperCase()}"`;
    showToast(toastMsg);

    // 4. Sync updated status to backend API
    try {
      await adminFetch(`/api/orders/${orderId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          status, 
          trackingNumber: trackingNum, 
          delhiveryWaybill: (orderObj as any)?.delhiveryWaybill || trackingNum,
          courierPartner: (orderObj as any)?.courierPartner || 'Delhivery',
          invoiceNumber: invoiceNum,
          isStockDeducted: stockDeductedNow ? true : (stockRestoredNow ? false : undefined),
          cancelledBy: effectiveCancelledBy,
          cancelledAt: effectiveCancelledAt,
          cancellationReason: effectiveCancellationReason
        })
      });
    } catch (e) {
      console.warn('Backend sync for order status update failed', e);
    }

    // 5. Trigger Order Confirmation Email ONLY on true state transition to 'confirmed'
    const previousStatus = targetOrder?.status;
    const isNewConfirmation = status === 'confirmed' && previousStatus !== 'confirmed';
    if (isNewConfirmation && targetOrder && !((orderObj as any)?.skipEmail)) {
      sendClientOrderEmail('confirmation', {
        ...targetOrder,
        status: 'confirmed',
        trackingNumber: trackingNum || targetOrder.trackingNumber,
        invoiceNumber: invoiceNum || targetOrder.invoiceNumber
      }).catch(err => console.warn('[Order Confirmation Email error]:', err));
    }

    // 6. Trigger Order Delivered Email ONLY on true state transition to 'delivered'
    const isNewDelivered = isDelivered && previousStatus !== 'delivered';
    if (isNewDelivered && targetOrder && !((orderObj as any)?.skipEmail)) {
      sendClientOrderEmail('delivered', {
        ...targetOrder,
        status: 'delivered',
        trackingNumber: trackingNum || targetOrder.trackingNumber,
        invoiceNumber: invoiceNum || targetOrder.invoiceNumber
      }).catch(err => console.warn('[Order Delivered Email error]:', err));
    }
  };

  const requestOrderReturn = async (
    orderId: string,
    returnType: 'return' | 'exchange',
    reason: string,
    details?: { selectedItemIds?: string[]; exchangeSize?: string; exchangeColor?: string; comments?: string }
  ) => {
    const returnStatusVal = returnType === 'return' ? 'return_requested' : ('exchange_requested' as any);
    const now = new Date().toISOString();
    const selectedIds = details?.selectedItemIds || [];

    setOrders(prev => {
      const updated = prev.map(ord => {
        if (ord.id === orderId) {
          const effectiveSelectedIds = selectedIds.length > 0
            ? selectedIds
            : (ord.items || []).map(i => i.id || i.productId);

          let calculatedRefund = 0;
          const updatedItems = (ord.items || []).map(item => {
            const itemId = item.id || item.productId;
            if (effectiveSelectedIds.includes(itemId)) {
              calculatedRefund += (item.price * item.quantity);
              return {
                ...item,
                returnStatus: returnStatusVal,
                returnType,
                returnReason: reason,
                returnComments: details?.comments,
                exchangeSize: details?.exchangeSize,
                exchangeColor: details?.exchangeColor,
              };
            }
            return item;
          });

          return {
            ...ord,
            items: updatedItems,
            returnedItemIds: effectiveSelectedIds,
            refundAmount: 0,
            approvedRefundAmount: 0,
            returnRefundAmount: calculatedRefund,
            pendingReturnItems: updatedItems.filter(i => effectiveSelectedIds.includes(i.id || i.productId)),
            acceptedReturnItems: [],
            rejectedReturnItems: [],
            returnStatus: returnStatusVal,
            returnType,
            returnReason: reason,
            returnComments: details?.comments,
            exchangeSize: details?.exchangeSize,
            exchangeColor: details?.exchangeColor,
            updatedAt: now
          };
        }
        return ord;
      });
      safeStorageSet('terra_orders_v2', JSON.stringify(updated));
      return updated;
    });

    try {
      const targetOrd = orders.find(o => o.id === orderId);
      await safeJsonFetch(`/api/orders/${orderId}/return`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerEmail: targetOrd?.customerEmail || user?.email,
          customerId: targetOrd?.customerId || user?.id,
          returnType,
          returnReason: reason,
          returnComments: details?.comments,
          exchangeSize: details?.exchangeSize,
          exchangeColor: details?.exchangeColor,
          selectedItemIds: selectedIds
        })
      });
    } catch (apiErr) {
      console.warn('REST API return request error:', apiErr);
    }

    try {
      window.dispatchEvent(new Event('pgmart_order_updated'));
    } catch {}

    showToast(`Return request submitted successfully! Doorstep reverse pickup will be scheduled.`);
  };

  const decideOrderReturn = async (
    orderId: string,
    decisions: Array<{ itemId: string; decision: 'accepted' | 'rejected'; id?: string; productId?: string; name?: string }>
  ) => {
    // 1. Optimistic Local State Update
    setOrders(prev => {
      const updated = prev.map(o => {
        if (o.id !== orderId) return o;
        const currentItems: any[] = Array.isArray(o.items) ? o.items : [];
        const decisionMap = new Map<string, string>();
        for (const d of decisions) {
          if (d.itemId) decisionMap.set(String(d.itemId), d.decision);
          if (d.id) decisionMap.set(String(d.id), d.decision);
          if (d.productId) decisionMap.set(String(d.productId), d.decision);
          if (d.name) decisionMap.set(String(d.name), d.decision);
        }

        const newItems = currentItems.map(item => {
          const matchKey = [item.id, item.productId, item.name, item.productName]
            .filter(Boolean)
            .map(String)
            .find(k => decisionMap.has(k));
          let dec = matchKey ? decisionMap.get(matchKey) : undefined;
          if (!dec && (item.returnStatus === 'return_requested' || item.returnStatus === 'exchange_requested')) {
            if (decisions.length === 1 || decisions.every(d => d.decision === decisions[0].decision)) {
              dec = decisions[0].decision;
            }
          }
          if (!dec) return item;

          const isEx = item.returnType === 'exchange';
          const newStatus = dec === 'accepted' ? (isEx ? 'exchange_accepted' : 'return_accepted') : (isEx ? 'exchange_rejected' : 'return_rejected');
          return {
            ...item,
            returnStatus: newStatus,
            refundAmount: dec === 'accepted' ? (Number(item.price || 0) * Number(item.quantity || 1)) : 0
          };
        });

        const pending = newItems.filter(i => i.returnStatus === 'return_requested' || i.returnStatus === 'exchange_requested');
        const accepted = newItems.filter(i => i.returnStatus === 'return_accepted' || i.returnStatus === 'exchange_accepted' || i.returnStatus === 'return_approved');
        const rejected = newItems.filter(i => i.returnStatus === 'return_rejected' || i.returnStatus === 'exchange_rejected' || i.returnStatus === 'rejected');
        const approvedRefund = accepted.reduce((s, i) => s + (Number(i.price || 0) * Number(i.quantity || 1)), 0);

        let ordStatus = o.returnStatus;
        if (pending.length > 0) {
          ordStatus = accepted.length > 0 ? 'partially_accepted' : 'return_requested';
        } else if (accepted.length > 0 && rejected.length > 0) {
          ordStatus = 'partially_accepted';
        } else if (accepted.length > 0) {
          ordStatus = 'return_accepted';
        } else if (rejected.length > 0) {
          ordStatus = 'return_rejected';
        }

        return {
          ...o,
          items: newItems,
          pendingReturnItems: pending,
          acceptedReturnItems: accepted,
          rejectedReturnItems: rejected,
          returnedItems: [...pending, ...accepted, ...rejected],
          returnStatus: ordStatus,
          refundAmount: approvedRefund,
          approvedRefundAmount: approvedRefund,
          updatedAt: new Date().toISOString()
        };
      });
      safeStorageSet('terra_orders_v2', JSON.stringify(updated));
      return updated;
    });

    // 2. Call Backend Express API
    try {
      const res = await adminFetch(`/api/orders/${orderId}/return/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decisions })
      });
      const contentType = res?.headers?.get('content-type') || '';
      if (res && res.ok && contentType.includes('application/json')) {
        const data = await res.json();
        if (data?.success && data?.order) {
          setOrders(prev => {
            const updated = prev.map(o => o.id === orderId ? { ...o, ...data.order } : o);
            safeStorageSet('terra_orders_v2', JSON.stringify(updated));
            return updated;
          });
        }
      }
    } catch (err: any) {
      console.warn('Backend decision API warning:', err);
    }

    showToast('Return decisions recorded successfully!');

    try {
      window.dispatchEvent(new Event('pgmart_order_updated'));
    } catch {}
  };

  const updateReturnStatus = async (
    orderId: string,
    returnStatus: 'approved' | 'rejected' | 'completed',
    adminNotes?: string
  ) => {
    const targetOrd = orders.find(o => o.id === orderId);
    const returnedItems = (targetOrd?.items || []).filter(i => i.returnStatus && i.returnStatus !== 'none');
    const itemsToDecide = returnedItems.length > 0 ? returnedItems : (targetOrd?.items || []);
    const decisions = itemsToDecide.map(i => ({
      itemId: i.id || i.productId,
      id: i.id,
      productId: i.productId,
      name: i.name || i.productName,
      decision: (returnStatus === 'approved' ? 'accepted' : 'rejected') as 'accepted' | 'rejected'
    }));

    await decideOrderReturn(orderId, decisions);
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  const contextValue = useMemo(() => ({
    settings,
    updateSettings,
    categories,
    setCategories,
    brands,
    setBrands,
    saveBrand,
    toggleBrand,
    deleteBrand,
    products,
    setProducts,
    banners,
    setBanners,
    isBannersLoaded,
    saveBanner,
    toggleBanner,
    deleteBanner,
    coupons,
    setCoupons,
    saveCoupon,
    toggleCoupon,
    deleteCoupon,
    cart,
    addToCart,
    removeFromCart,
    updateCartQty,
    clearCart,
    appliedCoupon,
    couponDiscount,
    applyCoupon,
    removeCoupon,
    wishlist,
    toggleWishlist,
    user,
    setUser,
    logoutUser,
    loginUser,
    isAdminLoggedIn,
    adminLogin,
    adminLogout,
    saveSettings,
    createProduct,
    updateProduct,
    updateProductStock,
    deleteProduct,
    orders,
    createOrder,
    updateOrderStatus,
    setOrderInvoiceNumber,
    requestOrderReturn,
    decideOrderReturn,
    updateReturnStatus,
    reviews,
    setReviews,
    addReview,
    updateReview,
    updateReviewStatus,
    deleteReview,
    refreshReviews,
    filters,
    setFilters,
    resetFilters,
    toastMessage,
    showToast,
    searchModalOpen,
    setSearchModalOpen,
    cartDrawerOpen,
    setCartDrawerOpen,
    sizeChartCategory,
    setSizeChartCategory,
    chatOpen,
    setChatOpen,
    reloadCatalog,
    blogPosts,
    setBlogPosts,
    saveBlogPost,
    deleteBlogPost,
    toggleBlogPostStatus,
    reloadBlogPosts,
    productDisplay,
    updateProductDisplay
  }), [
    settings,
    productDisplay,
    categories,
    brands,
    products,
    banners,
    coupons,
    cart,
    appliedCoupon,
    couponDiscount,
    wishlist,
    user,
    isAdminLoggedIn,
    orders,
    reviews,
    filters,
    toastMessage,
    searchModalOpen,
    cartDrawerOpen,
    sizeChartCategory,
    chatOpen,
    blogPosts
  ]);

  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
};
